import React, { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import dayjs from "dayjs";
import { get } from "../../api/usersModule/httpClient";
import { useDebounce } from "../../hooks/useDebounce";
import { LargeCard } from "../Cards/LargeCard";
import { CDBModal, CDBTable, CDBTableBody, CDBTableHeader, CDBSpinner } from "cdbreact";
import { ModalReports } from "../Modals/ModalReports";
import { Card, Row, Col, DatePicker, Select, Table as AntTable, Collapse } from "antd";
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Area,
  AreaChart,
  ResponsiveContainer,
  CartesianGrid,
  ReferenceLine,
  ScatterChart,
  Scatter,
  ZAxis,
} from "recharts";
import SkuInventoryLevelChart from "../Charts/SkuInventoryLevelChart";
import Swal from "sweetalert2";
import { RESET_REPORT_FILTERS } from "../../utils/Types";

const usersheaders = [
  "FECHA",
  "NIT",
  "PROVEEDOR",
  "CÓDIGO SAP",
  "DESCRIPCIÓN SKU",
  "INVENTARIO",
  "% INVENTARIO",
  "ROP",
  "SKU MIN",
  "SKU MAX",
  "CANTIDAD ENVIAR",
  "COLOR",
  "ÚLTIMA ACTUALIZACIÓN",
];

// Colores para los gráficos (mismos que backend-frontend)
const STATUS_HEX_COLORS = {
  Negro: "#1f2937",
  Rojo: "#dc2626",
  Amarillo: "#ca8a04",
  Verde: "#16a34a",
  Azul: "#2563eb",
  "Sin clasificar": "#9ca3af",
};

const STATUS_LABELS = {
  Negro: "Sin stock (<10%)",
  Rojo: "Stock bajo (10-30%)",
  Amarillo: "Precaución (30-70%)",
  Verde: "Óptimo (70-100%)",
  Azul: "Exceso (>100%)",
  "Sin clasificar": "N/A",
};

/**
 * Fuente única de clasificación de inventario para TODO este módulo.
 * Negro <10 | Rojo 10-30 | Amarillo >30-70 | Verde >70-100 | Azul >100
 */
const getInventoryColorLabel = (percent) => {
  const value = parseFloat(percent);
  if (Number.isNaN(value)) return "Sin clasificar";
  if (value < 10) return "Negro";
  if (value <= 30) return "Rojo";
  if (value <= 70) return "Amarillo";
  if (value <= 100) return "Verde";
  return "Azul";
};

const normalizeInventoryData = (items) => {
  if (!Array.isArray(items)) return [];
  return items.map((item) => ({
    ...item,
    ColorLabel: getInventoryColorLabel(item.PercentInventory),
  }));
};

const STATUS_ICONS = {
  Negro: "🔴",
  Rojo: "🟠",
  Amarillo: "🟡",
  Verde: "🟢",
  Azul: "🔵",
};

// Función para formatear fecha de YYYY-MM-DD a DD/MM
const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const [year, month, day] = dateStr.split("-");
  return `${day}/${month}`;
};

// Función para parsear fecha DD/MM para ordenamiento
const parseDate = (dateStr) => {
  const [day, month] = dateStr.split("/");
  return parseInt(`${month}${day}`, 10);
};

export const CrudProviderInventoryRecord = ({ userName, project }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(1);
  const [state, setState] = useState({ modal6: false });
  const [action] = useState("");
  const [initialRender, setInitialRender] = useState(true);
  const [providers, setProviders] = useState([]);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [filtersApplied, setFiltersApplied] = useState(false); // Nuevo estado para controlar si se aplicaron filtros iniciales
  const [loadingProviders, setLoadingProviders] = useState(true); // Estado para carga de proveedores
  const [chartProviderFilter, setChartProviderFilter] = useState([]); // Filtro de proveedor para los gráficos (array para selección múltiple)
  const [chartSkuFilter, setChartSkuFilter] = useState([]); // Filtro de SKU para el gráfico de evolución (array para selección múltiple)

  const defaultFormData = {
    selectedOptions: [],
    TimeStart: "",
    TimeEnd: "",
    selectedProviders: [],
    onlyActive: true,
  };

  const [formData, setFormData] = useState({
    ...defaultFormData,
  });

  const [pressedButton, setPressedButton] = useState("");

  // Leer filtros globales de Redux
  const globalFilters = useSelector(state => state.reportFilters?.providerInventoryDetail);
  const dispatch = useDispatch();

  /**
   * Efecto para aplicar filtros globales cuando están disponibles
   * Esto permite que los filtros se transfieran desde otros reportes
   */
  useEffect(() => {
    if (globalFilters && !filtersApplied && Object.keys(globalFilters).length > 0) {
      console.log("📥 Aplicando filtros globales en CrudProviderInventoryRecord:", globalFilters);

      // Las fechas vienen en formato DD/MM/YYYY desde Redux, que es el mismo formato que usa este componente
      // Solo necesitamos validar que existan ambos valores
      const hasValidDates = globalFilters.dateRange?.initialDate && globalFilters.dateRange?.finalDate;

      // Aplicar filtros globales al formulario local
      const newFormData = {
        ...formData,
        // Fechas desde filtros globales (ya vienen en formato DD/MM/YYYY)
        TimeStart: hasValidDates ? globalFilters.dateRange.initialDate : formData.TimeStart,
        TimeEnd: hasValidDates ? globalFilters.dateRange.finalDate : formData.TimeEnd,
        // Lista de proveedores
        selectedProviders: globalFilters.nitList || [],
        // Filtro de SKUs activos
        onlyActive: globalFilters.onlyActive ?? true,
      };

      setFormData(newFormData);

      // Marcar que se aplicaron los filtros ANTES de cargar datos
      // Esto evita que el useEffect de cambios dispare una segunda llamada
      setFiltersApplied(true);

      // Si vienen filtros globales con fechas válidas, cargar datos automáticamente
      // Usamos el formData actualizado directamente en lugar de esperar al setState
      if (hasValidDates) {
        console.log("🔄 Cargando datos con filtros globales...");
        // Llamar a getData con los nuevos filtros directamente
        const loadInitialData = async () => {
          try {
            setLoading(true);

            // Construir queryParams con los nuevos filtros
            const params = new URLSearchParams({
              reportType: "providerInventoryDetail",
              Nit: project,
              page: 0, // Primera página
              limit: itemsPerPage,
              onlyActive: newFormData.onlyActive ?? true,
              selectedInitialDate: newFormData.TimeStart,
              selectedFinalDate: newFormData.TimeEnd,
            });

            // Agregar proveedores si hay selección
            if (newFormData.selectedProviders && newFormData.selectedProviders.length > 0) {
              params.append("nitList", newFormData.selectedProviders.join(","));
            }

            console.log("🔍 Cargando datos iniciales con filtros globales:", Object.fromEntries(params));

            const response = await get(`/skusRecords?${params.toString()}`);

            console.log("📦 Respuesta del backend (filtros globales):", {
              hasAnswer: !!response?.answer,
              hasItems: !!response?.items,
              itemsCount: response?.items?.length || 0,
              pagination: response?.pagination,
            });

            if (response && response.items) {
              setData(normalizeInventoryData(response.items || []));
              setTotalItems(response.pagination?.totalItems || 0);
            } else {
              setData([]);
              setTotalItems(0);
            }

            // Marcar como 'loaded' para evitar que el useEffect de cambios dispare otra vez
            setLastTrigger('loaded');
            setDataLoadedFromGlobalFilters(true);
          } catch (error) {
            console.error("❌ Error cargando datos con filtros globales:", error);
            setData([]);
            setTotalItems(0);
          } finally {
            setLoading(false);
          }
        };

        loadInitialData();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalFilters]);

  const getData = async (searchParam, statusPage) => {
    try {
      setLoading(true);

      // Calcular fechas por defecto (7 días atrás hasta hoy) si no hay fechas seleccionadas
      const getInitialDate = () => {
        const today = new Date();
        const sevenDaysAgo = new Date(today);
        sevenDaysAgo.setDate(today.getDate() - 7);
        return `${String(sevenDaysAgo.getDate()).padStart(2, "0")}/${String(
          sevenDaysAgo.getMonth() + 1
        ).padStart(2, "0")}/${sevenDaysAgo.getFullYear()}`;
      };

      const getFinalDate = () => {
        const today = new Date();
        return `${String(today.getDate()).padStart(2, "0")}/${String(
          today.getMonth() + 1
        ).padStart(2, "0")}/${today.getFullYear()}`;
      };

      // Usar fechas del formulario o fechas por defecto
      const initialDate = formData.TimeStart || getInitialDate();
      const finalDate = formData.TimeEnd || getFinalDate();

      // Construir queryParams
      const params = new URLSearchParams({
        reportType: "providerInventoryDetail",
        Nit: project,
        page: statusPage - 1, // Backend usa 0-based
        limit: itemsPerPage,
        onlyActive: formData.onlyActive ?? true,
        selectedInitialDate: initialDate,
        selectedFinalDate: finalDate,
      });

      // Agregar proveedores si hay selección
      if (formData.selectedProviders && formData.selectedProviders.length > 0) {
        params.append("nitList", formData.selectedProviders.join(","));
      }

      // Search parameter
      if (searchParam) params.append("search", searchParam);

      console.log("🔍 Filtros enviando al backend:", {
        reportType: "providerInventoryDetail",
        Nit: project,
        selectedProviders: formData.selectedProviders,
        nitList: formData.selectedProviders?.join(","),
        initialDate,
        finalDate,
        onlyActive: formData.onlyActive,
      });

      const response = await get(`/skusRecords?${params.toString()}`);

      console.log("📦 Respuesta del backend:", {
        hasAnswer: !!response?.answer,
        hasItems: !!response?.items,
        itemsCount: response?.items?.length || 0,
        pagination: response?.pagination,
        firstItems: response?.items?.slice(0, 2),
      });

      if (response && response.answer) {
        setTotalItems(response.pagination?.totalItems || 0);
        setData(normalizeInventoryData(response.items || []));
      } else {
        setTotalItems(0);
        setData([]);
      }
      setLoading(false);
    } catch (error) {
      console.error("Error al obtener los datos:", error);
      setLoading(false);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Ocurrió un error al obtener los datos. Por favor, inténtalo de nuevo más tarde.",
        confirmButtonColor: "#146284",
      });
    }
  };

  const getProviders = async () => {
    try {
      const response = await get(`/projects?Nit=${project}&list=${true}`);
      if (response && response.items) {
        setProviders(response.items);
      } else {
        setProviders([]);
      }
    } catch (error) {
      console.error("Error fetching providers:", error);
      setProviders([]);
    }
  };

  const handleSubmit = () => {
    const requiredFields = formData.selectedOptions;

    const missingFields = requiredFields.filter((field) => {
      if (field === "selectedProviders") {
        return (
          !formData.selectedProviders || formData.selectedProviders.length === 0
        );
      }
      if (field === "onlyActive") {
        return formData.onlyActive === undefined || formData.onlyActive === "";
      }
      return !formData[field];
    });

    if (missingFields.length > 0) {
      Swal.fire({
        title: "Error!",
        text: "Por favor, rellene todos los campos",
        icon: "error",
        confirmButtonColor: "#146284",
      });
      setLoading(false);
      return false;
    }

    return true;
  };

  const toggle = (nr) => () => {
    let modalNumber = "modal" + nr;
    setState({ ...state, [modalNumber]: !state[modalNumber] });
  };

  const closeModal = () => {
    setState({ ...state, modal6: false });
  };

  const handleChange = (e) => {
    const value = e.target.value;

    // Manejar fechas de dayjs
    if (
      (e.target.id === "TimeStart" || e.target.id === "TimeEnd") &&
      value &&
      value.$d
    ) {
      const date = value.$d;
      const formattedDate =
        date instanceof Date && !isNaN(date)
          ? `${String(date.getDate()).padStart(2, "0")}/${String(
              date.getMonth() + 1
            ).padStart(2, "0")}/${date.getFullYear()}`
          : value;
      setFormData({ ...formData, [e.target.id]: formattedDate });
    } else if (e.target.id === "selectedOptions") {
      setFormData({ ...formData, [e.target.id]: value });
    } else {
      setFormData({ ...formData, [e.target.id]: value });
    }
  };

  const documentGenerate = async () => {
    try {
      if (formData.TimeStart && formData.TimeEnd) {
        const timeStart = new Date(formData.TimeStart);
        const timeEnd = new Date(formData.TimeEnd);

        formData.TimeStart = timeStart.toISOString().split("T")[0];
        formData.TimeEnd = timeEnd.toISOString().split("T")[0];
      }

      const params = new URLSearchParams({
        reportType: "providerInventoryDetail",
        format: "excel",
        Nit: project,
        onlyActive: formData.onlyActive ?? true,
      });

      if (
        formData.selectedProviders &&
        formData.selectedProviders.length > 0
      ) {
        params.append("nitList", formData.selectedProviders.join(","));
      }

      if (formData.TimeStart) params.append("selectedInitialDate", formData.TimeStart);
      if (formData.TimeEnd) params.append("selectedFinalDate", formData.TimeEnd);

      const response = await axios.get(
        `/skusRecords?${params.toString()}`
      );

      if (response.status === 200 && response.data.answer) {
        Swal.fire({
          title: "Generación Iniciada",
          text: "El reporte se está generando. Será redirigido cuando esté listo.",
          icon: "success",
          confirmButtonColor: "#146284",
        }).then(() => {
          if (response.data.url) {
            window.open(response.data.url, "_blank");
          }
        });
      } else {
        Swal.fire({
          title: "Error",
          text: "Error al generar el informe. Por favor, verifica la información.",
          icon: "error",
          confirmButtonColor: "#146284",
        });
      }
    } catch (error) {
      console.error("Error generando el informe:", error);
      Swal.fire({
        title: "Error",
        text: "Error al generar el informe. Por favor, verifica la información.",
        icon: "error",
        confirmButtonColor: "#146284",
      });
    }

    setFormData(defaultFormData);
  };

  const convertDataToArray = () => {
    // Validación robusta para evitar errores cuando data no es un array
    if (!data) return [];
    if (!Array.isArray(data)) return [];
    if (data.length === 0) return [];

    // Ordenar datos por Proveedor (ProjectName), luego por SKU (CodeSap), y finalmente por Fecha
    const sortedData = [...data].sort((a, b) => {
      const providerCompare = (a.ProjectName || "").toUpperCase().localeCompare((b.ProjectName || "").toUpperCase());
      if (providerCompare !== 0) return providerCompare;

      const skuCompare = (a.CodeSap || "").toUpperCase().localeCompare((b.CodeSap || "").toUpperCase());
      if (skuCompare !== 0) return skuCompare;

      // Ordenar por fecha si mismo SKU
      const dateCompare = (a.RecordDate || "").localeCompare(b.RecordDate || "");
      return dateCompare;
    });

    // Agrupar por proveedor y luego por SKU
    const result = [];
    let currentProvider = null;
    let currentSku = null;

    sortedData.forEach((item) => {
      const providerName = (item.ProjectName || "").toUpperCase();
      const nit = (item.Nit || "").toUpperCase();
      const skuCode = (item.CodeSap || "").toUpperCase();
      const skuDesc = (item.SkuDescription || "").toUpperCase();

      // Si cambia el proveedor, insertar fila de encabezado de proveedor
      if (providerName !== currentProvider) {
        currentProvider = providerName;
        currentSku = null; // Resetear SKU al cambiar de proveedor
        result.push({
          isProviderHeader: true,
          providerName: providerName,
          nit: nit,
        });
      }

      // Si cambia el SKU, insertar fila de encabezado de SKU
      if (skuCode !== currentSku) {
        currentSku = skuCode;
        result.push({
          isSkuHeader: true,
          skuCode: skuCode,
          skuDescription: skuDesc,
        });
      }

      // Fila de datos normal
      result.push({
        isProviderHeader: false,
        isSkuHeader: false,
        RecordDate: item.RecordDate || "",
        Nit: nit,
        ProjectName: providerName,
        CodeSap: skuCode,
        SkuDescription: skuDesc,
        Inventory: parseFloat(item.Inventory || 0).toFixed(2),
        PercentInventory: `${parseFloat(item.PercentInventory || 0).toFixed(2)}%`,
        Rop: parseFloat(item.Rop || 0).toFixed(2),
        SkuMin: parseFloat(item.SkuMin || 0).toFixed(2),
        SkuMax: parseFloat(item.SkuMax || 0).toFixed(2),
        SendQuantity: parseFloat(item.SendQuantity || 0).toFixed(2),
        ColorLabel: item.ColorLabel || "",
        LastUpdate: item.LastUpdate || "",
      });
    });

    return result;
  };

  // Función para aplicar los filtros iniciales
  const handleApplyInitialFilters = () => {
    if (!formData.selectedProviders || formData.selectedProviders.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "Atención",
        text: "Por favor, selecciona al menos un proveedor para continuar.",
        confirmButtonColor: "#146284",
      });
      return;
    }

    console.log("📊 Aplicando filtros:", {
      selectedProviders: formData.selectedProviders,
      TimeStart: formData.TimeStart,
      TimeEnd: formData.TimeEnd,
      onlyActive: formData.onlyActive,
    });

    // Resetear estados para forzar recarga completa
    setData([]);
    setTotalItems(0);
    setPage(1);
    setFiltersApplied(true);
    setLastTrigger('filters'); // Usar trigger específico para filtros
    setDataLoadedFromGlobalFilters(false);

    // Incrementar versión de filtros para forzar que useEffect se ejecute
    setFilterVersion(prev => prev + 1);
  };

  // Función para resetear filtros y volver al estado inicial
  const handleResetFilters = () => {
    setFiltersApplied(false);
    setData([]);
    setTotalItems(0);
    setPage(1);
    setFormData(defaultFormData);
    setChartProviderFilter([]);
    setChartSkuFilter([]);

    // Limpiar filtros globales de Redux
    dispatch({
      type: RESET_REPORT_FILTERS,
      payload: 'providerInventoryDetail'
    });
  };

  // Limpiar filtros globales de Redux al desmontar el componente
  useEffect(() => {
    return () => {
      dispatch({
        type: RESET_REPORT_FILTERS,
        payload: 'providerInventoryDetail'
      });
    };
  }, [dispatch]);

  // Cargar proveedores al inicio
  useEffect(() => {
    const loadProviders = async () => {
      setLoadingProviders(true);
      try {
        const response = await get(`/projects?Nit=${project}&list=${true}`);
        if (response && response.items) {
          setProviders(response.items);
        } else {
          setProviders([]);
        }
      } catch (error) {
        console.error("Error fetching providers:", error);
        setProviders([]);
      } finally {
        setLoadingProviders(false);
      }
    };

    loadProviders();
  }, [project]);

  // Estados para tracking de qué cambió (evitar múltiples llamadas)
  const [lastTrigger, setLastTrigger] = useState(null); // 'page' | 'search' | 'itemsPerPage' | 'filters' | null
  const [dataLoadedFromGlobalFilters, setDataLoadedFromGlobalFilters] = useState(false); // Evita doble carga cuando vienen filtros globales
  const [filterVersion, setFilterVersion] = useState(0); // Versión de filtros para forzar recargas

  // Efecto unificado para cargar datos - SOLO UNA llamada por cambio
  useEffect(() => {
    // Saltar el primer render
    if (initialRender) {
      setInitialRender(false);
      return;
    }

    // Solo cargar datos si se han aplicado los filtros iniciales
    if (!filtersApplied) {
      return;
    }

    // Si los datos ya fueron cargados desde filtros globales, no cargar de nuevo
    if (dataLoadedFromGlobalFilters && lastTrigger === 'loaded') {
      // Resetear la bandera después de que se procesó
      setDataLoadedFromGlobalFilters(false);
      return;
    }

    // No hay acción adicional necesaria

    // Determinar qué tipo de carga hacer basado en qué cambió
    if (lastTrigger === 'search' || lastTrigger === 'itemsPerPage') {
      // Resetear a página 1
      getData(debouncedSearch, 1);
      setPage(1);
      setLastTrigger(null);
    } else if (lastTrigger === 'page') {
      // Mantener página actual
      getData(debouncedSearch, page);
      setLastTrigger(null);
    } else if (lastTrigger === 'filters') {
      // Recarga por cambio de filtros manuales
      getData('', 1);
      setPage(1);
      setLastTrigger('loaded');
    } else if (lastTrigger === null && !dataLoadedFromGlobalFilters) {
      // Carga inicial después de aplicar filtros manualmente - cargar una sola vez
      // SOLO si no vienen de filtros globales
      getData('', page);
      setLastTrigger('loaded'); // Marcar como cargado
    }
  }, [page, debouncedSearch, itemsPerPage, initialRender, filtersApplied, lastTrigger, dataLoadedFromGlobalFilters, filterVersion]);

  // Efectos separados SOLO para actualizar lastTrigger (no llaman a getData directamente)
  useEffect(() => {
    if (!initialRender && filtersApplied && lastTrigger !== 'loaded') {
      setLastTrigger('page');
    }
  }, [page]);

  useEffect(() => {
    if (!initialRender && filtersApplied && lastTrigger !== 'loaded') {
      setLastTrigger('search');
    }
  }, [debouncedSearch]);

  useEffect(() => {
    if (!initialRender && filtersApplied && lastTrigger !== 'loaded') {
      setLastTrigger('itemsPerPage');
    }
  }, [itemsPerPage]);

  useEffect(() => {
    switch (pressedButton.split(",")[0]) {
      case "submitModal":
        const success = handleSubmit();
        if (success) {
          documentGenerate();
          toggle(6)();
        }
        break;
      case "refresh":
        getData("", page);
        break;
      case "reports":
        getProviders();
        toggle(6)();
        break;
      case "closeModal":
        toggle(6)();
        break;
      default:
        break;
    }
    setPressedButton("");
  }, [pressedButton]);

  // ============================================================
  // CHART COMPONENTS - MISMO CÓDIGO QUE BACKEND-FRONTEND
  // ============================================================

  // 1. ProviderDetailChart - Barras apiladas verticales (evolución por fecha)
  const ProviderDetailChart = ({ data: chartData, formatDate: formatDateFn }) => {
    const processedData = React.useMemo(() => {
      const groupedByDate = {};
      chartData.forEach((item) => {
        const date = item.RecordDate;
        if (!groupedByDate[date]) {
          groupedByDate[date] = {
            Negro: 0, Rojo: 0, Amarillo: 0, Verde: 0, Azul: 0, "Sin clasificar": 0,
          };
        }
        groupedByDate[date][item.ColorLabel]++;
      });

      return Object.entries(groupedByDate)
        .map(([date, colors]) => ({
          date: formatDateFn(date),
          ...colors,
          total: Object.values(colors).reduce((sum, count) => sum + count, 0),
        }))
        .sort((a, b) => {
          const dateA = parseDate(a.date);
          const dateB = parseDate(b.date);
          return dateA - dateB;
        });
    }, [chartData, formatDateFn]);

    if (processedData.length === 0) return null;

    return (
      <Card
        bordered={false}
        style={{ borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)", backgroundColor: "#ffffff" }}
        title={
          <div>
            <span style={{ fontSize: "18px", fontWeight: "600", color: "#111827" }}>¿Cómo se distribuye el inventario en el tiempo?</span>
            <div style={{ fontSize: "12px", color: "#6b7280", fontWeight: "400", marginTop: "4px" }}>
              Cantidad de SKUs por estado en cada fecha
            </div>
          </div>
        }
      >
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={processedData} layout="vertical" margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis type="number" stroke="#6b7280" />
            <YAxis dataKey="date" type="category" stroke="#6b7280" width={60} tick={{ fontSize: 12 }} />
            <Tooltip
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  const data = payload[0].payload;
                  return (
                    <div style={{ backgroundColor: "#ffffff", border: "1px solid #d1d5db", borderRadius: "8px", padding: "12px", boxShadow: "0 4px 6px rgba(0,0,0,0.1)" }}>
                      <p style={{ fontWeight: "600", color: "#111827", marginBottom: "8px" }}>Fecha: {data.date}</p>
                      {Object.keys(STATUS_HEX_COLORS).filter((key) => data[key] > 0).map((key) => (
                        <div key={key} style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "14px" }}>
                          <div style={{ width: "12px", height: "12px", borderRadius: "2px", backgroundColor: STATUS_HEX_COLORS[key] }} />
                          <span style={{ color: "#374151" }}>{STATUS_LABELS[key]}:</span>
                          <span style={{ fontWeight: "600", color: "#111827" }}>{data[key]}</span>
                        </div>
                      ))}
                      <div style={{ borderTop: "1px solid #e5e7eb", marginTop: "8px", paddingTop: "8px", fontSize: "14px" }}>
                        <span style={{ color: "#374151" }}>Total: </span>
                        <span style={{ fontWeight: "700", color: "#111827" }}>{data.total}</span>
                      </div>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Legend formatter={(value) => <span style={{ fontSize: "14px", color: "#374151" }}>{value}</span>} />
            <Bar dataKey="Negro" stackId="colors" fill={STATUS_HEX_COLORS.Negro} name={STATUS_LABELS.Negro} />
            <Bar dataKey="Rojo" stackId="colors" fill={STATUS_HEX_COLORS.Rojo} name={STATUS_LABELS.Rojo} />
            <Bar dataKey="Amarillo" stackId="colors" fill={STATUS_HEX_COLORS.Amarillo} name={STATUS_LABELS.Amarillo} />
            <Bar dataKey="Verde" stackId="colors" fill={STATUS_HEX_COLORS.Verde} name={STATUS_LABELS.Verde} />
            <Bar dataKey="Azul" stackId="colors" fill={STATUS_HEX_COLORS.Azul} name={STATUS_LABELS.Azul} />
            <Bar dataKey="Sin clasificar" stackId="colors" fill={STATUS_HEX_COLORS["Sin clasificar"]} name={STATUS_LABELS["Sin clasificar"]} />
          </BarChart>
        </ResponsiveContainer>
      </Card>
    );
  };

  // 2. SkuTimelineChart - Barras agrupadas por fecha con tooltip individual
  const SkuTimelineChart = ({ data: chartData, formatDate: formatDateFn, selectedSkus = [], onSkuChange = null }) => {
    console.log("📈 SkuTimelineChart - Datos recibidos:", {
      totalRegistros: chartData?.length || 0,
      muestra: chartData?.slice(0, 3) || [],
    });

    // Pivotar datos: una fila por fecha, columnas dinámicas por SKU×Proveedor
    const { pivotedData, barMeta, barKeys } = React.useMemo(() => {
      if (!chartData || chartData.length === 0) {
        return { pivotedData: [], barMeta: {}, barKeys: [] };
      }

      // Agrupar por SKU primero (mantener misma lógica de top 15)
      const skuGroups = {};
      chartData.forEach((item) => {
        if (!skuGroups[item.CodeSap]) {
          skuGroups[item.CodeSap] = {
            sku: item.CodeSap,
            description: item.SkuDescription,
            records: []
          };
        }
        skuGroups[item.CodeSap].records.push(item);
      });

      // Limitar a top 15 SKUs o usar los seleccionados
      const topSkus = selectedSkus && selectedSkus.length > 0
        ? selectedSkus
        : Object.keys(skuGroups)
            .sort((a, b) => skuGroups[b].records.length - skuGroups[a].records.length)
            .slice(0, 15);

      // Obtener fechas únicas ordenadas
      const allDates = Array.from(new Set(chartData.map((d) => d.RecordDate))).sort();

      // Construir metadatos y claves de barras
      const meta = {};
      const keySet = new Set();

      // Filtrar registros solo de los topSkus
      topSkus.forEach((sku) => {
        const skuInfo = skuGroups[sku];
        if (!skuInfo) return;
        skuInfo.records.forEach((record) => {
          const barKey = `${sku}__${record.Nit}`;
          keySet.add(barKey);
          // Guardar el registro más reciente para cada barKey (para color y tooltip)
          if (!meta[barKey] || record.RecordDate > meta[barKey].recordDate) {
            meta[barKey] = {
              sku: sku,
              description: skuInfo.description,
              colorLabel: record.ColorLabel,
              inventory: record.Inventory,
              percent: record.PercentInventory,
              rop: record.Rop,
              skuMin: record.SkuMin,
              skuMax: record.SkuMax,
              sendQuantity: record.SendQuantity,
              projectName: record.ProjectName,
              nit: record.Nit,
              recordDate: record.RecordDate,
            };
          }
        });
      });

      // Ordenar barKeys por SKU asc, luego ProjectName asc
      const keys = Array.from(keySet).sort((a, b) => {
        const metaA = meta[a];
        const metaB = meta[b];
        const skuCompare = metaA.sku.localeCompare(metaB.sku);
        if (skuCompare !== 0) return skuCompare;
        return (metaA.projectName || "").localeCompare(metaB.projectName || "");
      });

      // Pivotar: una fila por fecha
      const rows = allDates.map((date) => {
        const row = {
          recordDate: date,
          formattedDate: formatDateFn(date),
        };
        // Para cada barKey, buscar si hay registro en esta fecha
        keys.forEach((barKey) => {
          const [sku, nit] = barKey.split("__");
          const skuInfo = skuGroups[sku];
          if (!skuInfo) return;
          const record = skuInfo.records.find((r) => r.RecordDate === date && r.Nit === nit);
          if (record) {
            row[barKey] = record.PercentInventory;
            // Actualizar meta con el valor específico de esta fecha
            // (para que el tooltip muestre los datos correctos de cada barra)
            if (!meta[barKey + "_dates"]) {
              meta[barKey + "_dates"] = {};
            }
            meta[barKey + "_dates"][date] = {
              colorLabel: record.ColorLabel,
              inventory: record.Inventory,
              percent: record.PercentInventory,
              rop: record.Rop,
              skuMin: record.SkuMin,
              skuMax: record.SkuMax,
              sendQuantity: record.SendQuantity,
            };
          }
        });
        return row;
      });

      console.log("🔄 Datos pivotados:", rows.length, "fechas,", keys.length, "series");
      return { pivotedData: rows, barMeta: meta, barKeys: keys };
    }, [chartData, formatDateFn, selectedSkus]);

    // Obtener todos los SKUs únicos disponibles
    const allAvailableSkus = React.useMemo(() => {
      return Array.from(new Set(chartData.map((d) => d.CodeSap))).sort();
    }, [chartData]);

    if (pivotedData.length === 0 || barKeys.length === 0) {
      console.log("⚠️ SkuTimelineChart - No hay datos para mostrar");
      return null;
    }

    return (
      <Card
        bordered={false}
        style={{ borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)", backgroundColor: "#ffffff" }}
        title={
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" }}>
            <span style={{ fontSize: "18px", fontWeight: "600", color: "#111827" }}>Evolución de SKUs por Estado</span>
            <div style={{ fontSize: "12px", color: "#6b7280", fontWeight: "400" }}>
              Porcentaje de inventario por producto y fecha. Pasa el cursor para ver detalle.
            </div>
            <Select
              mode="multiple"
              style={{ minWidth: "200px", maxWidth: "300px" }}
              placeholder={selectedSkus.length === 0 ? "Todos los SKUs (top 15)" : "Seleccionar SKUs"}
              value={selectedSkus}
              onChange={(values) => onSkuChange?.(values)}
              allowClear
              showSearch
              maxTagCount="responsive"
              options={allAvailableSkus.map((sku) => ({
                value: sku,
                label: sku,
              }))}
              optionFilterProp="label"
              filterOption={(input, option) =>
                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
            />
          </div>
        }
      >
        <div style={{ height: "450px" }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={pivotedData} margin={{ top: 20, right: 30, left: 40, bottom: 60 }}
              barGap={2} barCategoryGap="20%">
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="formattedDate"
                stroke="#6b7280"
                fontSize={11}
                interval={0}
                angle={-30}
                textAnchor="end"
                height={60}
              />
              <YAxis
                stroke="#6b7280"
                fontSize={11}
                label={{ value: '% Inventario', angle: -90, position: 'insideLeft', style: { fontSize: '11px', fontWeight: '600' } }}
                tickFormatter={(value) => `${value}%`}
                domain={[0, 120]}
              />
              <Tooltip
                shared={false}
                content={({ active, payload }) => {
                  if (active && payload && payload.length > 0) {
                    const barKey = payload[0].dataKey;
                    const dateRow = payload[0].payload;
                    const meta = barMeta[barKey];
                    const dateMeta = barMeta[barKey + "_dates"]?.[dateRow.recordDate];

                    if (!meta) return null;

                    const colorLabel = dateMeta?.colorLabel || meta.colorLabel;
                    const inventory = dateMeta?.inventory ?? meta.inventory;
                    const percent = dateMeta?.percent ?? meta.percent;
                    const rop = dateMeta?.rop ?? meta.rop;
                    const skuMin = dateMeta?.skuMin ?? meta.skuMin;
                    const skuMax = dateMeta?.skuMax ?? meta.skuMax;
                    const sendQuantity = dateMeta?.sendQuantity ?? meta.sendQuantity;

                    return (
                      <div style={{
                        backgroundColor: "#ffffff",
                        border: "1px solid #d1d5db",
                        borderRadius: "8px",
                        padding: "14px",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                        minWidth: "260px",
                        fontSize: "13px"
                      }}>
                        {/* SKU y Descripción */}
                        <div style={{ marginBottom: "10px", paddingBottom: "8px", borderBottom: "1px solid #e5e7eb" }}>
                          <p style={{ fontWeight: "700", color: "#111827", margin: "0 0 4px 0", fontSize: "14px" }}>
                            {meta.sku}
                          </p>
                          <p style={{ color: "#6b7280", margin: 0, fontSize: "12px" }}>
                            {meta.description || "Sin descripción"}
                          </p>
                        </div>

                        {/* Proveedor */}
                        {meta.projectName && (
                          <div style={{ marginBottom: "8px" }}>
                            <span style={{ color: "#6b7280", fontSize: "11px", fontWeight: "500" }}>Proveedor: </span>
                            <span style={{ color: "#374151", fontWeight: "600" }}>{meta.projectName}</span>
                            {meta.nit && <span style={{ color: "#9ca3af", fontSize: "11px", marginLeft: "4px" }}>({meta.nit})</span>}
                          </div>
                        )}

                        {/* Fecha */}
                        <div style={{ marginBottom: "8px" }}>
                          <span style={{ color: "#6b7280", fontSize: "11px", fontWeight: "500" }}>Fecha: </span>
                          <span style={{ color: "#374151" }}>{dateRow.formattedDate}</span>
                        </div>

                        {/* Estado con color */}
                        <div style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          marginBottom: "10px",
                          padding: "6px 10px",
                          backgroundColor: `${STATUS_HEX_COLORS[colorLabel] || "#9ca3af"}15`,
                          borderRadius: "6px"
                        }}>
                          <span style={{
                            width: "14px",
                            height: "14px",
                            borderRadius: "3px",
                            backgroundColor: STATUS_HEX_COLORS[colorLabel] || "#9ca3af"
                          }}></span>
                          <span style={{
                            fontWeight: "700",
                            color: colorLabel === "Negro" || colorLabel === "Rojo" ? "#dc2626" : "#374151",
                            fontSize: "13px"
                          }}>
                            {STATUS_LABELS[colorLabel] || colorLabel}
                          </span>
                        </div>

                        {/* Métricas de inventario */}
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginTop: "10px" }}>
                          <div style={{ padding: "6px", backgroundColor: "#f9fafb", borderRadius: "4px" }}>
                            <div style={{ fontSize: "10px", color: "#6b7280", fontWeight: "500" }}>Inventario</div>
                            <div style={{ fontSize: "14px", fontWeight: "700", color: "#111827" }}>
                              {inventory?.toFixed(2) || "0"}
                            </div>
                          </div>
                          <div style={{ padding: "6px", backgroundColor: "#f9fafb", borderRadius: "4px" }}>
                            <div style={{ fontSize: "10px", color: "#6b7280", fontWeight: "500" }}>% Inventario</div>
                            <div style={{
                              fontSize: "14px",
                              fontWeight: "700",
                              color: STATUS_HEX_COLORS[colorLabel] || "#374151"
                            }}>
                              {percent?.toFixed(1) || "0"}%
                            </div>
                          </div>
                          <div style={{ padding: "6px", backgroundColor: "#f9fafb", borderRadius: "4px" }}>
                            <div style={{ fontSize: "10px", color: "#6b7280", fontWeight: "500" }}>ROP</div>
                            <div style={{ fontSize: "14px", fontWeight: "700", color: "#111827" }}>
                              {rop?.toFixed(2) || "0"}
                            </div>
                          </div>
                          <div style={{ padding: "6px", backgroundColor: "#f9fafb", borderRadius: "4px" }}>
                            <div style={{ fontSize: "10px", color: "#6b7280", fontWeight: "500" }}>Rango SKU</div>
                            <div style={{ fontSize: "13px", fontWeight: "600", color: "#374151" }}>
                              {skuMin?.toFixed(0) || "0"} - {skuMax?.toFixed(0) || "0"}
                            </div>
                          </div>
                        </div>

                        {/* Cantidad a enviar */}
                        {sendQuantity > 0 && (
                          <div style={{
                            marginTop: "10px",
                            padding: "8px",
                            backgroundColor: "#eff6ff",
                            borderRadius: "6px",
                            border: "1px solid #bfdbfe",
                            textAlign: "center"
                          }}>
                            <div style={{ fontSize: "11px", color: "#1e40af", fontWeight: "600" }}>Cantidad a Enviar</div>
                            <div style={{ fontSize: "18px", fontWeight: "800", color: "#1d4ed8" }}>
                              {sendQuantity?.toFixed(2)}
                            </div>
                          </div>
                        )}

                        {/* Sugerencia de acción */}
                        {(colorLabel === "Negro" || colorLabel === "Rojo") && (
                          <div style={{
                            marginTop: "10px",
                            padding: "8px 10px",
                            backgroundColor: "#fef2f2",
                            borderRadius: "6px",
                            border: "1px solid #fecaca",
                            fontSize: "12px",
                            color: "#991b1b",
                            fontWeight: "600",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}>
                            <span>💡</span>
                            {sendQuantity > 0
                              ? `Sugerencia: Reordenar ${sendQuantity?.toFixed(0)} unidades`
                              : "Sugerencia: Verificar punto de reorden"
                            }
                          </div>
                        )}
                        {colorLabel === "Azul" && (
                          <div style={{
                            marginTop: "10px",
                            padding: "8px 10px",
                            backgroundColor: "#eff6ff",
                            borderRadius: "6px",
                            border: "1px solid #bfdbfe",
                            fontSize: "12px",
                            color: "#1e40af",
                            fontWeight: "600",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}>
                            <span>💡</span>
                            Sugerencia: Evaluar reducción de inventario
                          </div>
                        )}
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Legend
                content={() => (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", justifyContent: "center", marginTop: "10px", maxHeight: "60px", overflow: "auto" }}>
                    {Object.entries(STATUS_HEX_COLORS).map(([key, color]) => (
                      <div key={key} style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                        <div style={{ width: "10px", height: "10px", borderRadius: "2px", backgroundColor: color }} />
                        <span style={{ fontSize: "11px", color: "#374151" }}>{STATUS_LABELS[key]}</span>
                      </div>
                    ))}
                  </div>
                )}
              />
              {barKeys.map((barKey) => {
                const meta = barMeta[barKey];
                const datesMeta = barMeta[barKey + "_dates"] || {};
                return (
                  <Bar
                    key={barKey}
                    dataKey={barKey}
                    name={meta ? `${meta.sku} - ${meta.projectName}` : barKey}
                    fill={STATUS_HEX_COLORS["Sin clasificar"]}
                    maxBarSize={16}
                  >
                    {pivotedData.map((dateRow, idx) => {
                      const dateInfo = datesMeta[dateRow.recordDate];
                      const colorKey = dateInfo?.colorLabel || "Sin clasificar";
                      return (
                        <Cell
                          key={idx}
                          fill={STATUS_HEX_COLORS[colorKey] || STATUS_HEX_COLORS["Sin clasificar"]}
                        />
                      );
                    })}
                  </Bar>
                );
              })}
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div style={{ marginTop: "12px", fontSize: "12px", color: "#6b7280", textAlign: "center" }}>
          Mostrando {barKeys.length} combinacion{barKeys.length !== 1 ? "es" : ""} en {pivotedData.length} fecha{pivotedData.length !== 1 ? "s" : ""} • Pasa el cursor para ver detalle
        </div>
      </Card>
    );
  };

  // 3. CriticalInventoryMatrix - Matriz de calor
  const CriticalInventoryMatrix = ({ data: matrixData, providerInfo = {}, dateRange = {} }) => {
    const [hoveredCell, setHoveredCell] = React.useState(null);

    const processedData = React.useMemo(() => {
      // Fuente única de color: ColorLabel recibido en los mismos registros
      // que alimentan la tabla "Detalle de Inventario por Proveedor".
      // No se recalculan rangos de porcentaje en esta gráfica.
      const criticalItems = matrixData || [];
      if (criticalItems.length === 0) return { matrix: [], providers: [], skus: [], dates: [] };

      // Obtener fechas únicas
      const uniqueDates = Array.from(new Set(criticalItems.map((item) => item.RecordDate))).sort();

      // Agrupar por SKU-Proveedor y mantener solo el registro más reciente de cada combinación
      const skuProviderMap = new Map();

      criticalItems.forEach((item) => {
        const key = `${item.CodeSap}_${item.ProjectName}`;
        const existing = skuProviderMap.get(key);

        // Si no existe o es más reciente
        if (!existing || item.RecordDate > existing.RecordDate) {
          skuProviderMap.set(key, item);
        }
      });

      const latestRecords = Array.from(skuProviderMap.values());

      // Obtener SKUs únicos (sin duplicar por fecha)
      const uniqueSkus = new Map();
      latestRecords.forEach((item) => {
        const existing = uniqueSkus.get(item.CodeSap);
        if (!existing || item.PercentInventory < existing.PercentInventory) {
          uniqueSkus.set(item.CodeSap, item);
        } else if (item.PercentInventory === existing.PercentInventory && item.SendQuantity > existing.SendQuantity) {
          uniqueSkus.set(item.CodeSap, item);
        }
      });

      const sortedSkus = Array.from(uniqueSkus.values())
        .sort((a, b) => {
          if (a.PercentInventory !== b.PercentInventory) return a.PercentInventory - b.PercentInventory;
          return b.SendQuantity - a.SendQuantity;
        })
        .slice(0, 20);

      const providerCriticalCount = latestRecords.reduce((acc, item) => {
        acc[item.ProjectName] = (acc[item.ProjectName] || 0) + 1;
        return acc;
      }, {});

      const providers = Array.from(new Set(latestRecords.map((item) => item.ProjectName)))
        .sort((a, b) => providerCriticalCount[b] - providerCriticalCount[a]);

      // Crear matriz: cada fila es un SKU único, cada columna es un proveedor
      const matrix = sortedSkus.map((sku) => {
        const row = { sku };
        providers.forEach((provider, index) => {
          const item = latestRecords.find((d) => d.ProjectName === provider && d.CodeSap === sku.CodeSap);
          row[`provider_${index}`] = item || null;
        });
        return row;
      });

      return { matrix, providers, skus: sortedSkus, dates: uniqueDates };
    }, [matrixData]);

    if (!processedData.matrix || processedData.matrix.length === 0) {
      return (
        <Card
          bordered={false}
          style={{ borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)", backgroundColor: "#ffffff" }}
        >
          <div style={{ padding: "32px", textAlign: "center" }}>
            <div style={{ fontSize: "48px", marginBottom: "16px" }}>✅</div>
            <p style={{ fontSize: "18px", fontWeight: "600", color: "#374151" }}>No hay SKUs críticos</p>
            <p style={{ fontSize: "14px", color: "#6b7280", marginTop: "8px" }}>Todos los SKUs están en estado saludable o aceptable</p>
          </div>
        </Card>
      );
    }

    const negroCount = processedData.skus.filter((s) => s.ColorLabel === "Negro").length;
    const rojoCount = processedData.skus.filter((s) => s.ColorLabel === "Rojo").length;
    const azulCount = processedData.skus.filter((s) => s.ColorLabel === "Azul").length;
    const withSendQuantity = processedData.skus.filter((s) => s.SendQuantity > 0).length;

    // Obtener nombres de proveedores seleccionados
    const getProviderNames = () => {
      if (providerInfo.selectedNits && providerInfo.selectedNits.length > 0 && providerInfo.allProviders) {
        return providerInfo.selectedNits.map(nit => {
          const provider = providerInfo.allProviders.find(p => p.Nit === nit);
          return provider ? (provider.ProjectName || provider.NameProject) : nit;
        }).join(", ");
      }
      return "Todos los proveedores";
    };

    return (
      <Card
        bordered={false}
        style={{
          borderRadius: "8px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          backgroundColor: "#ffffff",
          border: "1px solid #e5e7eb"
        }}
        title={
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
              <span style={{ fontSize: "20px" }}>⚠️</span>
              <span style={{ fontSize: "18px", fontWeight: "600", color: "#111827" }}>Productos que necesitan atención ahora</span>
            </div>
            {/* Información del reporte */}
            <div style={{ fontSize: "12px", color: "#6b7280", marginLeft: "28px" }}>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "16px", alignItems: "center" }}>
                <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                  <strong>Proveedor:</strong> {getProviderNames()}
                </span>
                {dateRange.initialDate && dateRange.finalDate && (
                  <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                    <strong>Periodo:</strong> {dateRange.initialDate} - {dateRange.finalDate}
                  </span>
                )}
                {processedData.dates.length > 0 && (
                  <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                    <strong>Última actualización:</strong> {processedData.dates[processedData.dates.length - 1]}
                  </span>
                )}
              </div>
            </div>
          </div>
        }
        headStyle={{
          backgroundColor: "linear-gradient(to bottom, #f9fafb, #ffffff)",
          borderBottom: "2px solid #e5e7eb"
        }}
      >
        {/* Estadísticas rápidas */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", marginBottom: "24px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "10px 18px", backgroundColor: "#fef2f2", borderRadius: "8px", border: "1px solid #fecaca", boxShadow: "0 1px 2px rgba(0,0,0,0.05)" }}>
            <span style={{ fontSize: "20px" }}>🔴</span>
            <div>
              <p style={{ fontSize: "20px", fontWeight: "700", color: "#b91c1c", margin: 0, lineHeight: 1 }}>{negroCount}</p>
              <p style={{ fontSize: "11px", color: "#dc2626", margin: 0, textTransform: "uppercase", letterSpacing: "0.5px" }}>Críticos</p>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "10px 18px", backgroundColor: "#fff7ed", borderRadius: "8px", border: "1px solid #fed7aa", boxShadow: "0 1px 2px rgba(0,0,0,0.05)" }}>
            <span style={{ fontSize: "20px" }}>🟠</span>
            <div>
              <p style={{ fontSize: "20px", fontWeight: "700", color: "#c2410c", margin: 0, lineHeight: 1 }}>{rojoCount}</p>
              <p style={{ fontSize: "11px", color: "#ea580c", margin: 0, textTransform: "uppercase", letterSpacing: "0.5px" }}>Alerta</p>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "10px 18px", backgroundColor: "#eff6ff", borderRadius: "8px", border: "1px solid #bfdbfe", boxShadow: "0 1px 2px rgba(0,0,0,0.05)" }}>
            <span style={{ fontSize: "20px" }}>🔵</span>
            <div>
              <p style={{ fontSize: "20px", fontWeight: "700", color: "#1d4ed8", margin: 0, lineHeight: 1 }}>{azulCount}</p>
              <p style={{ fontSize: "11px", color: "#2563eb", margin: 0, textTransform: "uppercase", letterSpacing: "0.5px" }}>Sobreinventario</p>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "10px 18px", backgroundColor: "#f0fdf4", borderRadius: "8px", border: "1px solid #bbf7d0", boxShadow: "0 1px 2px rgba(0,0,0,0.05)" }}>
            <span style={{ fontSize: "20px" }}>📦</span>
            <div>
              <p style={{ fontSize: "20px", fontWeight: "700", color: "#15803d", margin: 0, lineHeight: 1 }}>{withSendQuantity}</p>
              <p style={{ fontSize: "11px", color: "#16a34a", margin: 0, textTransform: "uppercase", letterSpacing: "0.5px" }}>Con envío</p>
            </div>
          </div>
          <div style={{ marginLeft: "auto", fontSize: "12px", color: "#6b7280", display: "flex", alignItems: "center" }}>
            {processedData.skus.length} SKUs analizados
          </div>
        </div>

        {/* Matriz */}
        <div style={{ overflowX: "auto", borderRadius: "8px", border: "1px solid #e5e7eb", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
          <table style={{ minWidth: "100%", fontSize: "13px", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ backgroundColor: "#1f2937" }}>
                <th style={{
                  padding: "14px 16px",
                  textAlign: "left",
                  fontWeight: "600",
                  color: "#ffffff",
                  position: "sticky",
                  left: 0,
                  backgroundColor: "#1f2937",
                  zIndex: 20,
                  minWidth: "300px",
                  fontSize: "12px",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  borderBottom: "2px solid #374151"
                }}>
                  📦 Referencia del Producto
                </th>
                {processedData.providers.map((provider) => (
                  <th key={provider} style={{
                    padding: "14px 12px",
                    textAlign: "center",
                    fontWeight: "600",
                    color: "#ffffff",
                    whiteSpace: "nowrap",
                    minWidth: "100px",
                    fontSize: "11px",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    borderLeft: "1px solid #374151"
                  }}>
                    {provider}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {processedData.matrix.map((row, rowIndex) => (
                <tr key={rowIndex} style={{
                  backgroundColor: rowIndex % 2 === 0 ? "#ffffff" : "#f9fafb",
                  borderBottom: "1px solid #e5e7eb"
                }}>
                  <td style={{
                    padding: "12px 16px",
                    fontWeight: "600",
                    color: "#111827",
                    position: "sticky",
                    left: 0,
                    zIndex: 10,
                    borderRight: "2px solid #e5e7eb",
                    backgroundColor: rowIndex % 2 === 0 ? "#ffffff" : "#f9fafb",
                    verticalAlign: "top"
                  }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                      {/* Código SKU completo */}
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <span style={{ color: "#9ca3af", fontSize: "12px" }}>│</span>
                        <span style={{ fontFamily: "monospace", fontSize: "12px", letterSpacing: "0.5px", fontWeight: "700" }}>
                          {row.sku.CodeSap}
                        </span>
                      </div>
                      {/* Descripción completa */}
                      <div style={{ paddingLeft: "20px" }}>
                        <span style={{ fontSize: "12px", color: "#374151", fontWeight: "400", lineHeight: 1.3 }}>
                          {row.sku.SkuDescription || "Sin descripción"}
                        </span>
                      </div>
                      {/* Estado y porcentaje */}
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", paddingLeft: "20px" }}>
                        {STATUS_ICONS[row.sku.ColorLabel]}
                        <span style={{ fontSize: "11px", color: "#6b7280", fontWeight: "600" }}>{row.sku.PercentInventory}% inventario</span>
                      </div>
                    </div>
                  </td>
                  {processedData.providers.map((provider, colIndex) => {
                    const key = `provider_${colIndex}`;
                    const item = row[key];
                    const isHovered = hoveredCell?.sku === row.sku.CodeSap && hoveredCell?.provider === provider;

                    return (
                      <td
                        key={key}
                        style={{
                          padding: "8px 6px",
                          textAlign: "center",
                          borderLeft: "1px solid #e5e7eb",
                          backgroundColor: item ? STATUS_HEX_COLORS[item.ColorLabel] : "#f9fafb",
                          transform: isHovered ? "scale(1.05)" : "scale(1)",
                          zIndex: isHovered ? 10 : 1,
                          boxShadow: isHovered ? "0 2px 8px rgba(0,0,0,0.2)" : "none",
                          transition: "all 0.15s ease-out",
                          cursor: item ? "pointer" : "default",
                          minWidth: "80px"
                        }}
                        onMouseEnter={() => item && setHoveredCell({ sku: row.sku.CodeSap, provider })}
                        onMouseLeave={() => setHoveredCell(null)}
                      >
                        {item ? (
                          <div style={{ position: "relative" }}>
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "2px" }}>
                              {item.SendQuantity > 0 ? (
                                <>
                                  <span style={{ color: "#ffffff", fontSize: "8px", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.5px" }}>Envío</span>
                                  <span style={{ color: "#ffffff", fontWeight: "900", fontSize: "15px", lineHeight: 1 }}>{item.SendQuantity}</span>
                                  {item.RecordDate && (
                                    <span style={{ color: "rgba(255,255,255,0.85)", fontSize: "8px", fontWeight: "500", whiteSpace: "nowrap" }}>
                                      {item.RecordDate}
                                    </span>
                                  )}
                                </>
                              ) : item.ColorLabel === "Azul" ? (
                                <>
                                  <span style={{ color: "#ffffff", fontSize: "8px", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.5px" }}>Sobreinventario</span>
                                  <span style={{ color: "#ffffff", fontWeight: "900", fontSize: "14px" }}>{item.PercentInventory}%</span>
                                  {item.RecordDate && (
                                    <span style={{ color: "rgba(255,255,255,0.85)", fontSize: "8px", fontWeight: "500", whiteSpace: "nowrap" }}>
                                      {item.RecordDate}
                                    </span>
                                  )}
                                </>
                              ) : (
                                <span style={{ color: "#ffffff", fontWeight: "900", fontSize: "16px" }}>{item.PercentInventory}%</span>
                              )}
                            </div>
                          </div>
                        ) : (
                          <span style={{ color: "#d1d5db", fontSize: "20px" }}>−</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Leyenda */}
        <div style={{
          marginTop: "16px",
          padding: "12px 16px",
          backgroundColor: "#f9fafb",
          borderRadius: "6px",
          border: "1px solid #e5e7eb",
          fontSize: "11px",
          color: "#6b7280"
        }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "16px", alignItems: "center" }}>
            <span style={{ fontWeight: "600", color: "#374151" }}>Leyenda:</span>
            <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span style={{ width: "12px", height: "12px", backgroundColor: "#1f2937", borderRadius: "2px" }}></span>
              Negro (&lt;10%)
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span style={{ width: "12px", height: "12px", backgroundColor: "#dc2626", borderRadius: "2px" }}></span>
              Rojo (10-30%)
            </span>
            <span style={{ marginLeft: "auto" }}>Pasa el cursor sobre las celdas para ampliar</span>
          </div>
        </div>
      </Card>
    );
  };

  // 4. RopComplianceChart - SKUs fuera de rango (solo última fecha)
  const RopComplianceChart = ({ data: chartData }) => {
    const processedData = React.useMemo(() => {
      // Agrupar por SKU y mantener solo el registro más reciente de cada uno
      const skuLatestMap = new Map();

      chartData.forEach((item) => {
        const existing = skuLatestMap.get(item.CodeSap);
        // Si no existe o es más reciente
        if (!existing || item.RecordDate > existing.RecordDate) {
          skuLatestMap.set(item.CodeSap, item);
        }
      });

      const latestRecords = Array.from(skuLatestMap.values());

      // Filtrar SKUs fuera de rango y mapear
      const outOfRange = latestRecords
        .filter((item) => item.Inventory < item.Rop || item.Inventory > item.SkuMax)
        .map((item) => ({
          sku: item.CodeSap,
          description: item.SkuDescription,
          inventory: item.Inventory,
          rop: item.Rop,
          skuMin: item.SkuMin,
          skuMax: item.SkuMax,
          gap: item.Rop - item.Inventory,
          sendQuantity: item.SendQuantity,
          colorLabel: item.ColorLabel,
          recordDate: item.RecordDate,
          projectName: item.ProjectName,
        }))
        .sort((a, b) => b.gap - a.gap)
        .slice(0, 20);

      return outOfRange;
    }, [chartData]);

    const getBarColor = (gap, colorLabel) => {
      // Mantener el mismo estado/color definido por ColorLabel en la tabla.
      return STATUS_HEX_COLORS[colorLabel] || "#9ca3af";
    };

    if (processedData.length === 0) {
      return (
        <Card
          bordered={false}
          style={{ borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)", backgroundColor: "#ffffff" }}
        >
          <div style={{ padding: "24px", textAlign: "center", color: "#6b7280" }}>
            <p>No hay SKUs fuera de rango para mostrar</p>
          </div>
        </Card>
      );
    }

    return (
      <Card
        bordered={false}
        style={{ borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)", backgroundColor: "#ffffff" }}
        title={
          <div>
            <span style={{ fontSize: "18px", fontWeight: "600", color: "#111827" }}>Productos por debajo del mínimo</span>
            <div style={{ fontSize: "12px", color: "#6b7280", fontWeight: "400", marginTop: "4px" }}>
              SKUs con inventario inferior al punto de reorden (ROP)
            </div>
          </div>
        }
      >
        <div style={{ height: "400px" }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={processedData} layout="vertical" margin={{ top: 20, right: 30, left: 120, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis type="number" stroke="#6b7280" fontSize={11} />
              <YAxis type="category" dataKey="sku" stroke="#6b7280" fontSize={10} width={110} tick={{ fontSize: 9 }} />
              <Tooltip
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    const d = payload[0].payload;
                    return (
                      <div style={{ backgroundColor: "#ffffff", border: "1px solid #d1d5db", borderRadius: "8px", padding: "12px", boxShadow: "0 4px 6px rgba(0,0,0,0.1)" }}>
                        <p style={{ fontWeight: "600", color: "#111827", marginBottom: "4px" }}>{d.sku}</p>
                        <p style={{ fontSize: "14px", color: "#6b7280" }}>{d.description}</p>
                        <div style={{ marginTop: "8px", fontSize: "14px" }}>
                          <p style={{ color: "#374151" }}>Inventario: <span style={{ fontWeight: "600" }}>{d.inventory}</span></p>
                          <p style={{ color: "#374151" }}>ROP: <span style={{ fontWeight: "600" }}>{d.rop}</span></p>
                          <p style={{ color: d.gap > 0 ? "#dc2626" : "#2563eb" }}>Gap: <span style={{ fontWeight: "600" }}>{d.gap > 0 ? `Faltan ${d.gap}` : `Sobran ${Math.abs(d.gap)}`}</span></p>
                          {d.sendQuantity > 0 && <p style={{ color: "#374151" }}>Enviar: <span style={{ fontWeight: "600", color: "#ea580c" }}>{d.sendQuantity}</span></p>}
                        </div>
                        {/* Sugerencia de acción */}
                        {d.gap > 0 && (
                          <div style={{
                            marginTop: "8px",
                            padding: "6px 8px",
                            backgroundColor: "#fef2f2",
                            borderRadius: "4px",
                            border: "1px solid #fecaca",
                            fontSize: "12px",
                            color: "#991b1b",
                            fontWeight: "600",
                          }}>
                            💡 {d.sendQuantity > 0 ? `Reordenar ${d.sendQuantity?.toFixed(0)} unidades` : "Verificar punto de reorden"}
                          </div>
                        )}
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <ReferenceLine x={0} stroke="#6b7280" strokeWidth={2} />
              <Bar dataKey="inventory" radius={[0, 4, 4, 0]} name="Inventario">
                {processedData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={getBarColor(entry.gap, entry.colorLabel)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>
    );
  };

  // 5. ProviderPerformanceChart - Desempeño por proveedor
  const ProviderPerformanceChart = ({ data: chartData }) => {
    const { processedData, dateRange, totalRecords } = React.useMemo(() => {
      // Obtener rango de fechas de los datos
      const allDates = chartData.map(d => d.RecordDate).filter(Boolean).sort();
      const minDate = allDates[0] || null;
      const maxDate = allDates[allDates.length - 1] || null;

      const grouped = chartData.reduce((acc, item) => {
        if (!acc[item.ProjectName]) {
          acc[item.ProjectName] = {
            provider: item.ProjectName,
            Negro: 0, Rojo: 0, Amarillo: 0, Verde: 0, Azul: 0, "Sin clasificar": 0, total: 0,
          };
        }
        acc[item.ProjectName][item.ColorLabel]++;
        acc[item.ProjectName].total++;
        return acc;
      }, {});

      const result = Object.values(grouped)
        .map((item) => {
          const total = item.total || 1;
          return {
            provider: item.provider,
            Negro: Math.round((item.Negro / total) * 100),
            Rojo: Math.round((item.Rojo / total) * 100),
            Amarillo: Math.round((item.Amarillo / total) * 100),
            Verde: Math.round((item.Verde / total) * 100),
            Azul: Math.round((item.Azul / total) * 100),
            total: item.total,
            criticalScore: item.Negro + item.Rojo + item.Azul,
          };
        })
        .sort((a, b) => a.criticalScore - b.criticalScore)
        .slice(0, 15);

      return { processedData: result, dateRange: { minDate, maxDate }, totalRecords: chartData.length };
    }, [chartData]);

    if (processedData.length === 0) {
      return (
        <Card
          bordered={false}
          style={{ borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)", backgroundColor: "#ffffff" }}
        >
          <div style={{ padding: "24px", textAlign: "center", color: "#6b7280" }}>
            <p>No hay datos de proveedores para mostrar</p>
          </div>
        </Card>
      );
    }

    const formatDateShort = (dateStr) => {
      if (!dateStr) return "";
      const [y, m, d] = dateStr.split("-");
      return `${d}/${m}/${y}`;
    };

    return (
      <Card
        bordered={false}
        style={{ borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)", backgroundColor: "#ffffff" }}
        title={
          <div>
            <span style={{ fontSize: "18px", fontWeight: "600", color: "#111827" }}>¿Qué tan buenos son mis proveedores?</span>
            <div style={{ fontSize: "12px", color: "#6b7280", fontWeight: "400", marginTop: "4px" }}>
              Basado en {totalRecords} registros
              {dateRange.minDate && dateRange.maxDate && (
                <> del {formatDateShort(dateRange.minDate)} al {formatDateShort(dateRange.maxDate)}</>
              )}
              {" · "}Distribución porcentual de colores por proveedor (todos los registros del periodo)
            </div>
          </div>
        }
      >
        <div style={{ height: "350px" }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={processedData} layout="vertical" margin={{ top: 20, right: 20, left: 10, bottom: 20 }} barCategoryGap={2}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis type="number" stroke="#6b7280" fontSize={10} domain={[0, 100]} tickFormatter={(value) => `${value}%`} />
              <YAxis type="category" dataKey="provider" stroke="#6b7280" fontSize={10} width={100} tick={{ fontSize: 9 }} />
              <Tooltip
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    const d = payload[0].payload;
                    return (
                      <div style={{ backgroundColor: "#ffffff", border: "1px solid #d1d5db", borderRadius: "8px", padding: "12px", boxShadow: "0 4px 6px rgba(0,0,0,0.1)" }}>
                        <p style={{ fontWeight: "600", color: "#111827", marginBottom: "8px" }}>{d.provider}</p>
                        {Object.keys(STATUS_LABELS).map((key) => {
                          const value = d[key];
                          if (value === undefined) return null;
                          return (
                            <div key={key} style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "14px" }}>
                              <div style={{ width: "12px", height: "12px", borderRadius: "2px", backgroundColor: STATUS_HEX_COLORS[key] }} />
                              <span style={{ color: "#374151" }}>{key}:</span>
                              <span style={{ fontWeight: "600", color: "#111827" }}>{value}%</span>
                            </div>
                          );
                        })}
                        <div style={{ borderTop: "1px solid #e5e7eb", marginTop: "8px", paddingTop: "8px", fontSize: "14px" }}>
                          <span style={{ color: "#374151" }}>Total SKUs: </span>
                          <span style={{ fontWeight: "700", color: "#111827" }}>{d.total}</span>
                        </div>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Legend wrapperStyle={{ fontSize: "10px" }} formatter={(value) => <span style={{ fontSize: "12px", color: "#374151" }}>{value}</span>} />
              <Bar dataKey="Negro" stackId="provider" fill={STATUS_HEX_COLORS.Negro} name="Negro" />
              <Bar dataKey="Rojo" stackId="provider" fill={STATUS_HEX_COLORS.Rojo} name="Rojo" />
              <Bar dataKey="Amarillo" stackId="provider" fill={STATUS_HEX_COLORS.Amarillo} name="Amarillo" />
              <Bar dataKey="Verde" stackId="provider" fill={STATUS_HEX_COLORS.Verde} name="Verde" />
              <Bar dataKey="Azul" stackId="provider" fill={STATUS_HEX_COLORS.Azul} name="Azul" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div style={{ marginTop: "16px", display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "12px", textAlign: "center", fontSize: "12px" }}>
          <div style={{ padding: "8px", backgroundColor: "#f0fdf4", borderRadius: "8px", border: "1px solid #bbf7d0" }}>
            <p style={{ fontWeight: "600", color: "#166534", margin: "0 0 4px 0" }}>{processedData.filter((d) => d.Negro + d.Rojo + d.Azul <= 10).length}</p>
            <p style={{ color: "#15803d", margin: 0 }}>Saludables (&lt;10% crítico/sobreinventario)</p>
          </div>
          <div style={{ padding: "8px", backgroundColor: "#fefce8", borderRadius: "8px", border: "1px solid #fef08a" }}>
            <p style={{ fontWeight: "600", color: "#a16207", margin: "0 0 4px 0" }}>{processedData.filter((d) => d.Negro + d.Rojo + d.Azul > 10 && d.Negro + d.Rojo + d.Azul <= 30).length}</p>
            <p style={{ color: "#ca8a04", margin: 0 }}>En Riesgo (10-30% crítico/sobreinventario)</p>
          </div>
          <div style={{ padding: "8px", backgroundColor: "#fef2f2", borderRadius: "8px", border: "1px solid #fecaca" }}>
            <p style={{ fontWeight: "600", color: "#991b1b", margin: "0 0 4px 0" }}>{processedData.filter((d) => d.Negro + d.Rojo + d.Azul > 30).length}</p>
            <p style={{ color: "#dc2626", margin: 0 }}>Críticos (&gt;30% crítico/sobreinventario)</p>
          </div>
        </div>
      </Card>
    );
  };

  // 6. BufferLevelsChart - AreaChart apilado al 100% (Niveles de Amortiguador)
  const BufferLevelsChart = ({ data: rawData }) => {
    const processedData = React.useMemo(() => {
      if (!rawData || rawData.length === 0) return [];

      // Paso 1: fechas únicas ordenadas
      const dates = [...new Set(rawData.map(d => d.RecordDate))].sort();

      // Paso 2: por cada fecha, contar SKUs por ColorLabel y calcular proporción
      const COLORES = ["Negro", "Rojo", "Amarillo", "Verde", "Azul"];

      return dates.map(date => {
        const skusDelDia = rawData.filter(d => d.RecordDate === date);
        const total = skusDelDia.length;
        const entry = { fecha: date };
        COLORES.forEach(color => {
          const count = skusDelDia.filter(d => d.ColorLabel === color).length;
          entry[color.toLowerCase()] = total > 0 ? count / total : 0;
        });
        return entry;
      });
    }, [rawData]);

    if (processedData.length === 0) {
      return (
        <Card
          bordered={false}
          style={{ borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)", backgroundColor: "#ffffff" }}
        >
          <div style={{ padding: "24px", textAlign: "center", color: "#6b7280" }}>
            <p>No hay datos para mostrar el gráfico de amortiguador</p>
          </div>
        </Card>
      );
    }

    const CustomTooltip = ({ active, payload, label }) => {
      if (!active || !payload?.length) return null;
      const [y, m, d] = label.split("-");
      // Invertir orden para mostrar azul arriba (como en el gráfico)
      const reversed = [...payload].reverse();
      return (
        <div style={{ background: "white", border: "1px solid #ccc", padding: 10, borderRadius: 6 }}>
          <p><strong>{`${d}/${m}/${y}`}</strong></p>
          {reversed.map(entry => (
            <p key={entry.dataKey} style={{ color: entry.fill === STATUS_HEX_COLORS.Amarillo ? "#999" : entry.fill, margin: "2px 0" }}>
              {entry.name}: <strong>{(entry.value * 100).toFixed(1)}%</strong>
            </p>
          ))}
        </div>
      );
    };

    return (
      <Card
        bordered={false}
        style={{ borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)", backgroundColor: "#ffffff" }}
        title={<span style={{ fontSize: "18px", fontWeight: "600", color: "#111827" }}>Niveles de Amortiguador Portal de Proveedores</span>}
      >
        <ResponsiveContainer width="100%" height={450}>
          <AreaChart data={processedData} margin={{ top: 20, right: 30, left: 40, bottom: 60 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="fecha"
              tickFormatter={(v) => {
                const [y, m, d] = v.split("-");
                return `${d}/${m}`;
              }}
              angle={-30}
              textAnchor="end"
              tickLine={false}
              interval={0}
            />
            <YAxis
              domain={[0, 1]}
              tickFormatter={(v) => `${(v * 100).toFixed(0)}%`}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend verticalAlign="bottom" />
            <Area dataKey="negro"    stackId="1" fill={STATUS_HEX_COLORS.Negro} stroke="none" fillOpacity={1} name={STATUS_LABELS.Negro} />
            <Area dataKey="rojo"     stackId="1" fill={STATUS_HEX_COLORS.Rojo} stroke="none" fillOpacity={1} name={STATUS_LABELS.Rojo} />
            <Area dataKey="amarillo" stackId="1" fill={STATUS_HEX_COLORS.Amarillo} stroke="none" fillOpacity={1} name={STATUS_LABELS.Amarillo} />
            <Area dataKey="verde"    stackId="1" fill={STATUS_HEX_COLORS.Verde} stroke="none" fillOpacity={1} name={STATUS_LABELS.Verde} />
            <Area dataKey="azul"     stackId="1" fill={STATUS_HEX_COLORS.Azul} stroke="none" fillOpacity={1} name={STATUS_LABELS.Azul} />
          </AreaChart>
        </ResponsiveContainer>
      </Card>
    );
  };

  // 7. InventoryHealthTimeline - Evolución de salud del inventario
  const InventoryHealthTimeline = ({ data: chartData, formatDate: formatDateFn }) => {
    const processedData = React.useMemo(() => {
      const grouped = chartData.reduce((acc, item) => {
        if (!acc[item.RecordDate]) {
          acc[item.RecordDate] = {
            Negro: 0, Rojo: 0, Amarillo: 0, Verde: 0, Azul: 0, "Sin clasificar": 0, total: 0,
          };
        }
        acc[item.RecordDate][item.ColorLabel]++;
        acc[item.RecordDate].total++;
        return acc;
      }, {});

      const result = Object.entries(grouped)
        .map(([date, counts]) => {
          const total = counts.total || 1;
          return {
            date,
            fechaCorta: formatDateFn(date),
            Negro: Math.round((counts.Negro / total) * 100),
            Rojo: Math.round((counts.Rojo / total) * 100),
            Amarillo: Math.round((counts.Amarillo / total) * 100),
            Verde: Math.round((counts.Verde / total) * 100),
            Azul: Math.round((counts.Azul / total) * 100),
            total: counts.total,
          };
        })
        .sort((a, b) => a.date.localeCompare(b.date));

      return result;
    }, [chartData, formatDateFn]);

    if (processedData.length === 0) {
      return (
        <Card
          bordered={false}
          style={{ borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)", backgroundColor: "#ffffff" }}
        >
          <div style={{ padding: "24px", textAlign: "center", color: "#6b7280" }}>
            <p>No hay datos de evolución para mostrar</p>
          </div>
        </Card>
      );
    }

    const latest = processedData[processedData.length - 1];
    const earliest = processedData[0];
    const healthChange = latest.Verde + latest.Amarillo - (earliest.Verde + earliest.Amarillo);
    const criticalChange = latest.Negro + latest.Rojo + latest.Azul - (earliest.Negro + earliest.Rojo + earliest.Azul);

    return (
      <Card
        bordered={false}
        style={{ borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)", backgroundColor: "#ffffff" }}
        title={
          <div>
            <span style={{ fontSize: "18px", fontWeight: "600", color: "#111827" }}>¿Mejora o empeora mi inventario?</span>
            <div style={{ fontSize: "12px", color: "#6b7280", fontWeight: "400", marginTop: "4px" }}>
              Proporción de SKUs por estado a lo largo del tiempo
            </div>
          </div>
        }
      >
        <div style={{ height: "300px" }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={processedData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="fechaCorta" stroke="#6b7280" fontSize={11} tick={{ fontSize: 10 }} />
              <YAxis stroke="#6b7280" fontSize={11} tickFormatter={(value) => `${value}%`} domain={[0, 100]} />
              <Tooltip
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    const d = payload[0].payload;
                    return (
                      <div style={{ backgroundColor: "#ffffff", border: "1px solid #d1d5db", borderRadius: "8px", padding: "12px", boxShadow: "0 4px 6px rgba(0,0,0,0.1)" }}>
                        <p style={{ fontWeight: "600", color: "#111827", marginBottom: "8px" }}>Fecha: {d.fechaCorta}</p>
                        {Object.keys(STATUS_LABELS).map((key) => {
                          const value = d[key];
                          if (value === undefined || value === 0) return null;
                          return (
                            <div key={key} style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "14px" }}>
                              <div style={{ width: "12px", height: "12px", borderRadius: "2px", backgroundColor: STATUS_HEX_COLORS[key] }} />
                              <span style={{ color: "#374151" }}>{STATUS_LABELS[key]}:</span>
                              <span style={{ fontWeight: "600", color: "#111827" }}>{value}%</span>
                            </div>
                          );
                        })}
                        <div style={{ borderTop: "1px solid #e5e7eb", marginTop: "8px", paddingTop: "8px", fontSize: "14px" }}>
                          <span style={{ color: "#374151" }}>Total SKUs: </span>
                          <span style={{ fontWeight: "700", color: "#111827" }}>{d.total}</span>
                        </div>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Legend wrapperStyle={{ fontSize: "10px" }} formatter={(value) => <span style={{ fontSize: "12px", color: "#374151" }}>{value}</span>} />
              <Area type="monotone" dataKey="Negro" stackId="health" stroke={STATUS_HEX_COLORS.Negro} fill={STATUS_HEX_COLORS.Negro} fillOpacity={0.7} name={STATUS_LABELS.Negro} />
              <Area type="monotone" dataKey="Rojo" stackId="health" stroke={STATUS_HEX_COLORS.Rojo} fill={STATUS_HEX_COLORS.Rojo} fillOpacity={0.7} name={STATUS_LABELS.Rojo} />
              <Area type="monotone" dataKey="Amarillo" stackId="health" stroke={STATUS_HEX_COLORS.Amarillo} fill={STATUS_HEX_COLORS.Amarillo} fillOpacity={0.7} name={STATUS_LABELS.Amarillo} />
              <Area type="monotone" dataKey="Verde" stackId="health" stroke={STATUS_HEX_COLORS.Verde} fill={STATUS_HEX_COLORS.Verde} fillOpacity={0.7} name={STATUS_LABELS.Verde} />
              <Area type="monotone" dataKey="Azul" stackId="health" stroke={STATUS_HEX_COLORS.Azul} fill={STATUS_HEX_COLORS.Azul} fillOpacity={0.7} name={STATUS_LABELS.Azul} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div style={{ marginTop: "16px", display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "12px", textAlign: "center", fontSize: "12px" }}>
          <div style={{ padding: "8px", backgroundColor: "#f9fafb", borderRadius: "8px", border: "1px solid #e5e7eb" }}>
            <p style={{ fontWeight: "600", color: "#111827", margin: "0 0 4px 0" }}>{processedData.length}</p>
            <p style={{ color: "#6b7280", margin: 0 }}>Días analizados</p>
          </div>
          <div style={{ padding: "8px", backgroundColor: "#f9fafb", borderRadius: "8px", border: "1px solid #e5e7eb" }}>
            <p style={{ fontWeight: "600", color: healthChange >= 0 ? "#16a34a" : "#dc2626", margin: "0 0 4px 0" }}>{healthChange >= 0 ? "+" : ""}{healthChange}%</p>
            <p style={{ color: "#6b7280", margin: 0 }}>Cambio Saludable</p>
          </div>
          <div style={{ padding: "8px", backgroundColor: "#f9fafb", borderRadius: "8px", border: "1px solid #e5e7eb" }}>
            <p style={{ fontWeight: "600", color: criticalChange <= 0 ? "#16a34a" : "#dc2626", margin: "0 0 4px 0" }}>{criticalChange >= 0 ? "+" : ""}{criticalChange}%</p>
            <p style={{ color: "#6b7280", margin: 0 }}>Cambio Crítico/Sobreinventario</p>
          </div>
          <div style={{ padding: "8px", backgroundColor: "#f9fafb", borderRadius: "8px", border: "1px solid #e5e7eb" }}>
            <p style={{ fontWeight: "600", color: "#111827", margin: "0 0 4px 0" }}>{latest.Verde + latest.Amarillo}%</p>
            <p style={{ color: "#6b7280", margin: 0 }}>Salud Actual</p>
          </div>
        </div>
      </Card>
    );
  };

  // 7. ProviderSkuVelocity - Análisis de riesgo por proveedor (solo última fecha)
  const ProviderSkuVelocity = ({ data: chartData }) => {
    const processedData = React.useMemo(() => {
      // Agrupar por SKU-Proveedor y mantener solo el registro más reciente de cada uno
      const skuProviderMap = new Map();

      chartData.forEach((item) => {
        const key = `${item.ProjectName}_${item.CodeSap}`;
        const existing = skuProviderMap.get(key);
        // Si no existe o es más reciente
        if (!existing || item.RecordDate > existing.RecordDate) {
          skuProviderMap.set(key, item);
        }
      });

      const latestRecords = Array.from(skuProviderMap.values());

      // Obtener la fecha más reciente global para mostrar en el título
      const latestDate = latestRecords.length > 0
        ? latestRecords.reduce((latest, item) => item.RecordDate > latest ? item.RecordDate : latest, "")
        : null;

      const grouped = latestRecords.reduce((acc, item) => {
        if (!acc[item.ProjectName]) {
          acc[item.ProjectName] = {
            provider: item.ProjectName,
            totalPercentInventory: 0,
            skuCount: 0,
            totalSendQuantity: 0,
            colorCounts: { Negro: 0, Rojo: 0, Amarillo: 0, Verde: 0, Azul: 0 },
          };
        }
        acc[item.ProjectName].totalPercentInventory += item.PercentInventory;
        acc[item.ProjectName].skuCount += 1;
        acc[item.ProjectName].totalSendQuantity += item.SendQuantity;
        acc[item.ProjectName].colorCounts[item.ColorLabel]++;
        return acc;
      }, {});

      const result = Object.values(grouped).map((item) => {
        const avgPercentInventory = item.totalPercentInventory / item.skuCount;
        const weights = { Verde: 100, Amarillo: 75, Azul: 25, Rojo: 25, Negro: 0, "Sin clasificar": 50 };
        let weightedSum = 0;
        let totalCount = 0;
        Object.entries(item.colorCounts).forEach(([color, count]) => {
          weightedSum += count * (weights[color] || 50);
          totalCount += count;
        });
        const healthScore = totalCount > 0 ? weightedSum / totalCount : 50;

        return {
          provider: item.provider,
          avgPercentInventory,
          skuCount: item.skuCount,
          totalSendQuantity: item.totalSendQuantity,
          healthScore,
        };
      });

      return { result, latestDate };
    }, [chartData]);

    if (!processedData || processedData.result.length === 0) {
      return (
        <Card
          bordered={false}
          style={{ borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)", backgroundColor: "#ffffff" }}
        >
          <div style={{ padding: "24px", textAlign: "center", color: "#6b7280" }}>
            <p>No hay datos de proveedores para mostrar</p>
          </div>
        </Card>
      );
    }

    const { result: dataResult, latestDate } = processedData;

    const avgSkuCount = dataResult.reduce((sum, d) => sum + d.skuCount, 0) / dataResult.length;
    const avgPercent = dataResult.reduce((sum, d) => sum + d.avgPercentInventory, 0) / dataResult.length;

    const getHealthColor = (score) => {
      if (score >= 70) return "#16a34a";
      if (score >= 50) return "#ca8a04";
      if (score >= 30) return "#dc2626";
      return "#1f2937";
    };

    return (
      <Card
        bordered={false}
        style={{ borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)", backgroundColor: "#ffffff" }}
        title={
          <div>
            <span style={{ fontSize: "18px", fontWeight: "600", color: "#111827" }}>Análisis de Riesgo por Proveedor</span>
            {latestDate && (
              <span style={{ fontSize: "12px", color: "#6b7280", marginLeft: "12px", fontWeight: "400" }}>
                📅 {latestDate}
              </span>
            )}
          </div>
        }
      >
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "8px", marginBottom: "16px", fontSize: "12px" }}>
          <div style={{ padding: "8px", backgroundColor: "#fef2f2", borderRadius: "8px", border: "1px solid #fecaca", textAlign: "center" }}>
            <p style={{ fontWeight: "600", color: "#991b1b", margin: "0 0 4px 0" }}>Alto Riesgo</p>
            <p style={{ color: "#dc2626", margin: 0 }}>Muchos SKUs, bajo inventario</p>
          </div>
          <div style={{ padding: "8px", backgroundColor: "#fff7ed", borderRadius: "8px", border: "1px solid #fed7aa", textAlign: "center" }}>
            <p style={{ fontWeight: "600", color: "#9a3412", margin: "0 0 4px 0" }}>Riesgo Medio</p>
            <p style={{ color: "#ea580c", margin: 0 }}>Pocos SKUs, bajo inventario</p>
          </div>
          <div style={{ padding: "8px", backgroundColor: "#f0fdf4", borderRadius: "8px", border: "1px solid #bbf7d0", textAlign: "center" }}>
            <p style={{ fontWeight: "600", color: "#166534", margin: "0 0 4px 0" }}>Saludable</p>
            <p style={{ color: "#16a34a", margin: 0 }}>Pocos SKUs, inventario en rango</p>
          </div>
          <div style={{ padding: "8px", backgroundColor: "#eff6ff", borderRadius: "8px", border: "1px solid #bfdbfe", textAlign: "center" }}>
            <p style={{ fontWeight: "600", color: "#1e40af", margin: "0 0 4px 0" }}>Sobreinventario</p>
            <p style={{ color: "#2563eb", margin: 0 }}>Muchos SKUs, inventario excesivo</p>
          </div>
        </div>
        <div style={{ height: "350px" }}>
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 60 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis type="number" dataKey="skuCount" name="SKUs" stroke="#6b7280" fontSize={11} label={{ value: "Número de SKUs", position: "insideBottom", offset: -5, fontSize: 11, fill: "#6b7280" }} />
              <YAxis type="number" dataKey="avgPercentInventory" name="Promedio Inventario" stroke="#6b7280" fontSize={11} label={{ value: "% Inventario Promedio", angle: -90, position: "insideLeft", fontSize: 11, fill: "#6b7280" }} />
              <ZAxis type="number" dataKey="totalSendQuantity" range={[50, 400]} name="Cantidad a Enviar" />
              <Tooltip
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    const d = payload[0].payload;
                    return (
                      <div style={{ backgroundColor: "#ffffff", border: "1px solid #d1d5db", borderRadius: "8px", padding: "12px", boxShadow: "0 4px 6px rgba(0,0,0,0.1)" }}>
                        <p style={{ fontWeight: "600", color: "#111827", marginBottom: "8px" }}>{d.provider}</p>
                        <div style={{ marginTop: "8px", fontSize: "14px" }}>
                          <p style={{ color: "#374151" }}>SKUs: <span style={{ fontWeight: "600" }}>{d.skuCount}</span></p>
                          <p style={{ color: "#374151" }}>Promedio Inventario: <span style={{ fontWeight: "600" }}>{d.avgPercentInventory.toFixed(1)}%</span></p>
                          <p style={{ color: "#374151" }}>Total a Enviar: <span style={{ fontWeight: "600" }}>{d.totalSendQuantity.toLocaleString()}</span></p>
                          <p style={{ color: d.healthScore >= 70 ? "#16a34a" : d.healthScore >= 50 ? "#ca8a04" : "#dc2626" }}>Health Score: <span style={{ fontWeight: "600" }}>{d.healthScore.toFixed(0)}/100</span></p>
                        </div>
                      </div>
                    );
                  }
                  return null;
                }}
                cursor={{ strokeDasharray: "3 3" }}
              />
              <ReferenceLine x={avgSkuCount} stroke="#9ca3af" strokeDasharray="3 3" />
              <ReferenceLine y={avgPercent} stroke="#9ca3af" strokeDasharray="3 3" />
              <Scatter data={dataResult} fill="#16a34a">
                {dataResult.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={getHealthColor(entry.healthScore)} />
                ))}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
        </div>
        <div style={{ marginTop: "16px", display: "flex", flexWrap: "wrap", gap: "12px", justifyContent: "center", alignItems: "center", fontSize: "12px" }}>
          <span style={{ color: "#6b7280" }}>Score de Salud:</span>
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <div style={{ width: "12px", height: "12px", borderRadius: "50%", backgroundColor: "#16a34a" }} />
            <span style={{ color: "#374151" }}>≥70 (Excelente)</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <div style={{ width: "12px", height: "12px", borderRadius: "50%", backgroundColor: "#ca8a04" }} />
            <span style={{ color: "#374151" }}>50-69 (Aceptable)</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <div style={{ width: "12px", height: "12px", borderRadius: "50%", backgroundColor: "#dc2626" }} />
            <span style={{ color: "#374151" }}>30-49 (Riesgoso)</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <div style={{ width: "12px", height: "12px", borderRadius: "50%", backgroundColor: "#1f2937" }} />
            <span style={{ color: "#374151" }}>&lt;30 (Crítico)</span>
          </div>
        </div>
      </Card>
    );
  };

  // Datos filtrados para los gráficos según los proveedores seleccionados
  const filteredChartData = React.useMemo(() => {
    if (!chartProviderFilter || chartProviderFilter.length === 0) return data;

    const selectedNits = chartProviderFilter.map((nit) => String(nit));
    return data.filter((item) =>
      selectedNits.includes(String(item.Nit))
    );
  }, [data, chartProviderFilter]);

  // KPI summary calculations
  const kpiData = React.useMemo(() => {
    if (!filteredChartData || filteredChartData.length === 0) return null;

    // Get latest and earliest dates
    const allDates = [...new Set(filteredChartData.map(d => d.RecordDate))].sort();
    const latestDate = allDates[allDates.length - 1];
    const earliestDate = allDates[0];

    // Latest date records
    const latestRecords = filteredChartData.filter(d => d.RecordDate === latestDate);
    const earliestRecords = filteredChartData.filter(d => d.RecordDate === earliestDate);

    // Count by color for latest date
    const countByColor = (records) => {
      const counts = { Negro: 0, Rojo: 0, Amarillo: 0, Verde: 0, Azul: 0, "Sin clasificar": 0 };
      records.forEach(r => { counts[r.ColorLabel] = (counts[r.ColorLabel] || 0) + 1; });
      return counts;
    };

    const latestCounts = countByColor(latestRecords);
    const earliestCounts = countByColor(earliestRecords);
    const totalLatest = latestRecords.length || 1;

    // Health score: weighted average
    const weights = { Verde: 100, Amarillo: 75, Azul: 50, Rojo: 25, Negro: 0, "Sin clasificar": 50 };
    let weightedSum = 0;
    Object.entries(latestCounts).forEach(([color, count]) => {
      weightedSum += count * (weights[color] || 50);
    });
    const healthScore = Math.round(weightedSum / totalLatest);

    // Out of ROP count (latest date)
    const outOfRop = latestRecords.filter(r => r.Inventory < r.Rop).length;

    // Trends (latest vs earliest)
    const criticalLatest = latestCounts.Negro + latestCounts.Rojo;
    const criticalEarliest = earliestCounts.Negro + earliestCounts.Rojo;
    const healthyLatest = latestCounts.Verde + latestCounts.Amarillo;
    const healthyEarliest = earliestCounts.Verde + earliestCounts.Amarillo;

    return {
      healthScore,
      criticalCount: criticalLatest,
      criticalTrend: criticalLatest - criticalEarliest,
      healthyCount: healthyLatest,
      healthyTrend: healthyLatest - healthyEarliest,
      outOfRop,
      overstockCount: latestCounts.Azul,
      total: totalLatest,
      latestCounts,
    };
  }, [filteredChartData]);

  // Traffic Light Status Bar component
  const TrafficLightBar = ({ kpiInfo }) => {
    if (!kpiInfo) return null;
    const { latestCounts, total } = kpiInfo;
    const colors = [
      { key: 'Azul', color: STATUS_HEX_COLORS.Azul, label: 'Exceso' },
      { key: 'Verde', color: STATUS_HEX_COLORS.Verde, label: 'Óptimo' },
      { key: 'Amarillo', color: STATUS_HEX_COLORS.Amarillo, label: 'Precaución' },
      { key: 'Rojo', color: STATUS_HEX_COLORS.Rojo, label: 'Stock bajo' },
      { key: 'Negro', color: STATUS_HEX_COLORS.Negro, label: 'Sin stock' },
    ];

    return (
      <div style={{
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        padding: '16px 20px',
        border: '1px solid #e5e7eb',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        marginBottom: '20px',
      }}>
        <div style={{ fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '10px' }}>
          Distribución actual del inventario
        </div>
        <div style={{ display: 'flex', height: '32px', borderRadius: '6px', overflow: 'hidden', marginBottom: '8px' }}>
          {colors.map(({ key, color }) => {
            const count = latestCounts[key] || 0;
            const pct = total > 0 ? (count / total) * 100 : 0;
            if (pct === 0) return null;
            return (
              <div
                key={key}
                style={{
                  width: `${pct}%`,
                  backgroundColor: color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#ffffff',
                  fontSize: '11px',
                  fontWeight: 700,
                  minWidth: pct > 3 ? '24px' : '0',
                }}
              >
                {pct >= 8 ? `${Math.round(pct)}%` : ''}
              </div>
            );
          })}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'center' }}>
          {colors.map(({ key, color, label }) => (
            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: '#6b7280' }}>
              <div style={{ width: '10px', height: '10px', borderRadius: '2px', backgroundColor: color }} />
              <span>{label}: <strong style={{ color: '#374151' }}>{latestCounts[key] || 0}</strong></span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // KPI Card component
  const KpiCard = ({ icon, label, value, trend, suffix = '', bgColor, valueColor, trendInverted = false }) => {
    const trendColor = trendInverted
      ? (trend > 0 ? '#dc2626' : trend < 0 ? '#16a34a' : '#9ca3af')
      : (trend > 0 ? '#16a34a' : trend < 0 ? '#dc2626' : '#9ca3af');
    const trendIcon = trend > 0 ? '↑' : trend < 0 ? '↓' : '→';

    return (
      <div style={{
        backgroundColor: bgColor || '#ffffff',
        borderRadius: '12px',
        padding: '14px 16px',
        border: '1px solid #e5e7eb',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        textAlign: 'center',
        height: '100%',
      }}>
        <div style={{ fontSize: '20px', marginBottom: '4px' }}>{icon}</div>
        <div style={{ fontSize: '13px', fontWeight: 500, color: '#6b7280', marginBottom: '4px' }}>{label}</div>
        <div style={{ fontSize: '24px', fontWeight: 700, color: valueColor || '#111827', lineHeight: 1.2 }}>
          {value}{suffix}
        </div>
        {trend !== undefined && trend !== 0 && (
          <div style={{ fontSize: '11px', fontWeight: 600, color: trendColor, marginTop: '4px' }}>
            {trendIcon} {trend > 0 ? '+' : ''}{trend} vs inicio
          </div>
        )}
      </div>
    );
  };

  // JSX para los gráficos - LAYOUT REORGANIZADO CON KPIs Y COLLAPSE
  const chartsSection = data && data.length > 0 ? (
    <div style={{ marginBottom: "24px" }}>
      {/* KPI Summary Cards */}
      {kpiData && (
        <>
          <Row gutter={[12, 12]} style={{ marginBottom: "16px" }}>
            <Col xs={12} sm={8} md={4}>
              <KpiCard
                icon={kpiData.healthScore >= 60 ? "🟢" : kpiData.healthScore >= 40 ? "🟡" : "🔴"}
                label="Salud General"
                value={kpiData.healthScore}
                suffix="/100"
                valueColor={kpiData.healthScore >= 60 ? "#16a34a" : kpiData.healthScore >= 40 ? "#ca8a04" : "#dc2626"}
              />
            </Col>
            <Col xs={12} sm={8} md={4}>
              <KpiCard
                icon="🔴"
                label="SKUs Críticos"
                value={kpiData.criticalCount}
                trend={kpiData.criticalTrend}
                bgColor="#fef2f2"
                valueColor="#b91c1c"
                trendInverted={true}
              />
            </Col>
            <Col xs={12} sm={8} md={4}>
              <KpiCard
                icon="🟢"
                label="SKUs Saludables"
                value={kpiData.healthyCount}
                trend={kpiData.healthyTrend}
                bgColor="#f0fdf4"
                valueColor="#166534"
              />
            </Col>
            <Col xs={12} sm={8} md={4}>
              <KpiCard
                icon="⚠️"
                label="Fuera de ROP"
                value={kpiData.outOfRop}
                bgColor="#fff7ed"
                valueColor="#9a3412"
              />
            </Col>
            <Col xs={12} sm={8} md={4}>
              <KpiCard
                icon="📦"
                label="Sobreinventario"
                value={kpiData.overstockCount}
                bgColor="#eff6ff"
                valueColor="#1e40af"
              />
            </Col>
          </Row>

          {/* Traffic Light Status Bar */}
          <TrafficLightBar kpiInfo={kpiData} />
        </>
      )}

      {/* Provider filter for charts */}
      <div style={{
        backgroundColor: "#f8fafc",
        border: "1px solid #e2e8f0",
        borderRadius: "12px",
        padding: "16px",
        marginBottom: "20px",
      }}>
        <div style={{ marginBottom: "12px" }}>
          <span style={{ fontSize: "15px", color: "#1f2937", fontWeight: "700" }}>Proveedores en gráficos:</span>
          <span style={{ fontSize: "13px", color: "#6b7280", marginLeft: "8px", fontWeight: "400" }}>
            {chartProviderFilter.length === 0
              ? "(Mostrando todos los proveedores del filtro principal)"
              : `(${chartProviderFilter.length} proveedor${chartProviderFilter.length > 1 ? "es" : ""} seleccionado${chartProviderFilter.length > 1 ? "s" : ""})`
            }
          </span>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: chartProviderFilter.length > 0 ? "12px" : "0" }}>
          <Select
            mode="multiple"
            style={{ minWidth: "350px", maxWidth: "100%", flex: 1 }}
            placeholder="Selecciona proveedores para filtrar los gráficos..."
            value={chartProviderFilter}
            onChange={(values) => setChartProviderFilter(values)}
            allowClear
            showSearch
            maxTagCount="responsive"
            maxTagPlaceholder={(omittedValues) => `y ${omittedValues.length} más...`}
            tagRender={(props) => {
              const { label, value, closable, onClose } = props;
              return (
                <span style={{
                  display: "inline-flex",
                  alignItems: "center",
                  backgroundColor: "#eff6ff",
                  border: "1px solid #3b82f6",
                  color: "#1e40af",
                  borderRadius: "8px",
                  padding: "4px 10px",
                  fontSize: "13px",
                  fontWeight: "500",
                  margin: "3px",
                  maxWidth: "180px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  boxShadow: "0 1px 2px rgba(59, 130, 246, 0.1)"
                }}>
                  {label}
                  {closable && (
                    <span
                      onClick={onClose}
                      style={{ marginLeft: "6px", cursor: "pointer", fontSize: "16px", fontWeight: "bold" }}
                    >
                      ×
                    </span>
                  )}
                </span>
              );
            }}
            options={(() => {
              // Mostrar los mismos proveedores seleccionados en el filtro principal.
              // Se normalizan los NIT como texto para evitar problemas number/string.
              const selectedNits = Array.isArray(formData.selectedProviders)
                ? formData.selectedProviders.map((nit) => String(nit))
                : [];

              return selectedNits.map((nit) => {
                const provider = providers.find(
                  (p) => String(p.Nit) === nit
                );

                return {
                  value: nit,
                  label: provider?.ProjectName || provider?.NameProject || nit,
                };
              });
            })()}
            optionFilterProp="label"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            notFoundContent="No hay proveedores disponibles"
            dropdownStyle={{ maxHeight: "300px" }}
          />
        </div>
      </div>

      {/* Charts organized in collapsible sections */}
      <Collapse
        defaultActiveKey={["evolution", "critical"]}
        ghost
        expandIconPosition="start"
        style={{ background: 'transparent', border: 'none' }}
        items={[
          {
            key: "evolution",
            label: (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '16px' }}>📈</span>
                <span style={{ fontSize: '16px', fontWeight: 600, color: '#146284' }}>¿Cómo va mi inventario?</span>
                <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: 400 }}>Evolución y tendencia general</span>
              </div>
            ),
            children: (
              <Row gutter={[16, 16]}>
                <Col span={24}>
                  <InventoryHealthTimeline data={filteredChartData} formatDate={formatDate} />
                </Col>
                <Col span={24}>
                  <ProviderDetailChart data={filteredChartData} formatDate={formatDate} />
                </Col>
              </Row>
            ),
            style: { marginBottom: '8px', backgroundColor: '#ffffff', borderRadius: '12px', border: '1px solid #e5e7eb' },
          },
          {
            key: "critical",
            label: (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '16px' }}>⚠️</span>
                <span style={{ fontSize: '16px', fontWeight: 600, color: '#146284' }}>SKUs que requieren atención</span>
                <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: 400 }}>Productos críticos y fuera de rango</span>
              </div>
            ),
            children: (
              <Row gutter={[16, 16]}>
                <Col xs={24} lg={12}>
                  <CriticalInventoryMatrix
                    data={filteredChartData}
                    providerInfo={{
                      selectedNits: chartProviderFilter.length > 0 ? chartProviderFilter : (formData.selectedProviders || []),
                      allProviders: providers
                    }}
                    dateRange={{
                      initialDate: formData.TimeStart,
                      finalDate: formData.TimeEnd
                    }}
                  />
                </Col>
                <Col xs={24} lg={12}>
                  <RopComplianceChart data={filteredChartData} />
                </Col>
                <Col span={24}>
                  <SkuTimelineChart
                    data={filteredChartData}
                    formatDate={formatDate}
                    selectedSkus={chartSkuFilter}
                    onSkuChange={setChartSkuFilter}
                  />
                </Col>
              </Row>
            ),
            style: { marginBottom: '8px', backgroundColor: '#ffffff', borderRadius: '12px', border: '1px solid #e5e7eb' },
          },
          {
            key: "detail",
            label: (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '16px' }}>🔍</span>
                <span style={{ fontSize: '16px', fontWeight: 600, color: '#146284' }}>Detalle por SKU y Proveedor</span>
                <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: 400 }}>Nivel individual y comparativa</span>
              </div>
            ),
            children: (
              <Row gutter={[16, 16]}>
                <Col span={24}>
                  <SkuInventoryLevelChart
                    data={filteredChartData}
                    selectedSkus={chartSkuFilter}
                    onSkuChange={setChartSkuFilter}
                  />
                </Col>
                <Col span={24}>
                  <ProviderPerformanceChart data={filteredChartData} />
                </Col>
              </Row>
            ),
            style: { marginBottom: '8px', backgroundColor: '#ffffff', borderRadius: '12px', border: '1px solid #e5e7eb' },
          },
        ]}
      />
    </div>
  ) : null;

  // Componente de filtro inicial
  const InitialFiltersCard = () => (
    <>
      {/* Hero Header */}
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: '20px',
        padding: '48px 40px',
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        marginBottom: '24px',
        boxShadow: '0 10px 40px rgba(102, 126, 234, 0.2)'
      }}>
        <div style={{
          position: 'absolute',
          top: '-50%',
          right: '-10%',
          width: '300px',
          height: '300px',
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '50%',
          filter: 'blur(40px)'
        }} />
        <div style={{
          position: 'absolute',
          bottom: '-30%',
          left: '-5%',
          width: '200px',
          height: '200px',
          background: 'rgba(255, 255, 255, 0.08)',
          borderRadius: '50%',
          filter: 'blur(30px)'
        }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '64px',
            height: '64px',
            borderRadius: '16px',
            background: 'rgba(255, 255, 255, 0.2)',
            marginBottom: '20px',
            fontSize: '32px'
          }}>
            📊
          </div>
          <h2 style={{
            color: 'white',
            marginBottom: '12px',
            fontWeight: 700,
            fontSize: '28px'
          }}>
            Detalle de Inventario por Proveedor
          </h2>
          <p style={{
            color: 'rgba(255, 255, 255, 0.9)',
            fontSize: '16px',
            marginBottom: 0,
            maxWidth: '600px'
          }}>
            Análisis detallado de SKUs por proveedor con evolución temporal, métricas de salud y alertas de stockout
          </p>
        </div>
      </div>

      {/* Filters Card */}
      <Card
        bordered={false}
        style={{
          width: "100%",
          margin: "0 0 24px 0",
          borderRadius: "16px",
          boxShadow: "0 2px 12px rgba(0, 0, 0, 0.06)",
          border: "1px solid #e5e7eb",
          backgroundColor: "#ffffff",
        }}
        bodyStyle={{ padding: '24px' }}
      >

      <Row gutter={[16, 16]}>
        {/* Selector de Proveedores */}
        <Col xs={24} lg={12}>
          <label style={{ display: "block", fontWeight: "500", color: "#374151", marginBottom: "8px", fontSize: "14px" }}>
            Proveedores <span style={{ color: "#dc2626" }}>*</span>
          </label>
          <Select
            mode="multiple"
            placeholder="Selecciona uno o más proveedores"
            style={{ width: "100%" }}
            value={formData.selectedProviders}
            onChange={(selectedOptions) => {
              setFormData({
                ...formData,
                selectedProviders: selectedOptions,
              });
            }}
            options={providers.map((p) => ({
              value: p.Nit,
              label: `${p.ProjectName || p.NameProject} (${p.Nit})`,
            }))}
            optionLabelProp="label"
            optionFilterProp="label"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            loading={loadingProviders}
            showSearch
            allowClear
          />
          {formData.selectedProviders.length > 0 && (
            <div style={{
              marginTop: "12px",
              fontSize: "13px",
              color: "#6b7280",
              display: "flex",
              flexWrap: "wrap",
              gap: "8px"
            }}>
              <span style={{ fontWeight: "600" }}>Proveedores seleccionados:</span>
              {formData.selectedProviders.map((nit) => {
                const provider = providers.find((p) => p.Nit === nit);
                return (
                  <span key={nit} style={{
                    padding: "4px 10px",
                    backgroundColor: "#f3f4f6",
                    borderRadius: "16px",
                    fontSize: "12px",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px"
                  }}>
                    {provider?.ProjectName || provider?.NameProject || nit}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setFormData({
                          ...formData,
                          selectedProviders: formData.selectedProviders.filter((p) => p !== nit),
                        });
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#6b7280",
                        cursor: "pointer",
                        padding: "0",
                        fontSize: "14px",
                        lineHeight: "1",
                        borderRadius: "50%",
                        width: "16px",
                        height: "16px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                      }}
                      title="Remover"
                    >
                      ×
                    </button>
                  </span>
                );
              })}
            </div>
          )}
        </Col>

        {/* Checkbox SKUs Activos */}
        <Col xs={24} lg={12}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb', height: '100%' }}>
            <span style={{ fontWeight: 500, color: '#374151', fontSize: '14px' }}>
              Solo SKUs Activos
            </span>
            <input
              type="checkbox"
              checked={formData.onlyActive ?? true}
              onChange={(e) => setFormData({ ...formData, onlyActive: e.target.checked })}
              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
            />
          </div>
        </Col>

        {/* Fecha Inicial */}
        <Col xs={24} sm={12}>
          <label style={{ display: "block", fontWeight: "500", color: "#374151", marginBottom: "8px", fontSize: "14px" }}>
            Fecha Inicial
          </label>
          <DatePicker
            style={{ width: "100%" }}
            placeholder="DD/MM/YYYY"
            format="DD/MM/YYYY"
            value={formData.TimeStart ? dayjs(formData.TimeStart, "DD/MM/YYYY") : null}
            onChange={(date) => {
              if (date) {
                const formattedDate = date.format("DD/MM/YYYY");
                setFormData({ ...formData, TimeStart: formattedDate });
              } else {
                setFormData({ ...formData, TimeStart: "" });
              }
            }}
          />
        </Col>

        {/* Fecha Final */}
        <Col xs={24} sm={12}>
          <label style={{ display: "block", fontWeight: "500", color: "#374151", marginBottom: "8px", fontSize: "14px" }}>
            Fecha Final
          </label>
          <DatePicker
            style={{ width: "100%" }}
            placeholder="DD/MM/YYYY"
            format="DD/MM/YYYY"
            value={formData.TimeEnd ? dayjs(formData.TimeEnd, "DD/MM/YYYY") : null}
            onChange={(date) => {
              if (date) {
                const formattedDate = date.format("DD/MM/YYYY");
                setFormData({ ...formData, TimeEnd: formattedDate });
              } else {
                setFormData({ ...formData, TimeEnd: "" });
              }
            }}
          />
        </Col>

        {/* Info de fechas y botones */}
        <Col xs={24}>
          {/* Info de fechas por defecto */}
          <div style={{
            padding: "12px 16px",
            backgroundColor: "#eff6ff",
            borderRadius: "8px",
            border: "1px solid #bfdbfe",
            fontSize: "14px",
            color: "#1e40af",
            marginBottom: "16px"
          }}>
            ℹ️ Si no seleccionas fechas, se usarán los últimos 7 días por defecto.
          </div>

          {/* Botones de acción */}
          <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
          <button
            onClick={() => setFormData(defaultFormData)}
            style={{
              padding: "10px 20px",
              borderRadius: "6px",
              border: "1px solid #d1d5db",
              backgroundColor: "#ffffff",
              color: "#374151",
              fontWeight: "600",
              cursor: "pointer",
              fontSize: "14px",
            }}
          >
            Limpiar
          </button>
          <button
            onClick={handleApplyInitialFilters}
            disabled={!formData.selectedProviders || formData.selectedProviders.length === 0}
            style={{
              padding: "10px 24px",
              borderRadius: "8px",
              border: "none",
              background: (!formData.selectedProviders || formData.selectedProviders.length === 0)
                ? "#9ca3af"
                : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "#ffffff",
              fontWeight: "600",
              cursor: (!formData.selectedProviders || formData.selectedProviders.length === 0) ? "not-allowed" : "pointer",
              fontSize: "14px",
              transition: "all 0.2s ease",
              boxShadow: (!formData.selectedProviders || formData.selectedProviders.length === 0)
                ? "none"
                : "0 4px 12px rgba(102, 126, 234, 0.3)"
            }}
            onMouseEnter={(e) => {
              if (formData.selectedProviders && formData.selectedProviders.length > 0) {
                e.target.style.transform = "translateY(-2px)";
                e.target.style.boxShadow = "0 6px 16px rgba(102, 126, 234, 0.4)";
              }
            }}
            onMouseLeave={(e) => {
              if (formData.selectedProviders && formData.selectedProviders.length > 0) {
                e.target.style.transform = "translateY(0)";
                e.target.style.boxShadow = "0 4px 12px rgba(102, 126, 234, 0.3)";
              }
            }}
          >
            Aplicar Filtros
          </button>
        </div>
      </Col>
    </Row>
    </Card>
    </>
  );

  // Return principal del componente CrudProviderInventoryRecord
  return (
    <>
      {/* Mostrar filtro inicial si no se han aplicado filtros */}
      {!filtersApplied ? (
        <InitialFiltersCard />
      ) : (
        <>
          {/* Botón para cambiar filtros */}
          <div style={{ padding: "16px 0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <button
              onClick={handleResetFilters}
              style={{
                padding: "8px 16px",
                borderRadius: "8px",
                border: "1px solid #d1d5db",
                backgroundColor: "#ffffff",
                color: "#374151",
                fontWeight: "500",
                cursor: "pointer",
                fontSize: "14px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                transition: "all 0.2s ease",
                boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#f9fafb";
                e.target.style.borderColor = "#9ca3af";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "#ffffff";
                e.target.style.borderColor = "#d1d5db";
              }}
            >
              ← Cambiar Filtros
            </button>
            <div style={{ fontSize: "14px", color: "#6b7280", fontWeight: "500" }}>
              {formData.selectedProviders?.length > 0 && (
                <span>
                  <strong style={{ color: "#374151" }}>Proveedores:</strong> {formData.selectedProviders.length} seleccionado
                  {formData.selectedProviders.length !== 1 && "s"}
                </span>
              )}
            </div>
          </div>

          {chartsSection}
          <GroupedProviderTable
            data={convertDataToArray()}
            loading={loading}
            headers={usersheaders}
          />
        </>
      )}
      <CDBModal isOpen={state.modal6} toggle={toggle(6)}>
        <ModalReports
          action={action}
          setPressedButton={setPressedButton}
          formData={formData}
          closeModal={closeModal}
          handleChange={handleChange}
          origin={"PROVIDER_INVENTORY"}
          providers={providers}
        />
      </CDBModal>
    </>
  );
};


const GroupedProviderTable = function({ data, loading, headers }) {
  const expandedProvidersHook = useState({});
  const expandedSkusHook = useState({});
  const [expandedProviders, setExpandedProviders] = expandedProvidersHook;
  const [expandedSkus, setExpandedSkus] = expandedSkusHook;

  if (loading) {
    return (
      <div style={{ padding: "48px", textAlign: "center", backgroundColor: "#ffffff", borderRadius: "16px", border: "1px solid #e5e7eb" }}>
        <div style={{
          width: "48px",
          height: "48px",
          margin: "0 auto 16px",
          border: "4px solid #e5e7eb",
          borderTopColor: "#667eea",
          borderRadius: "50%",
          animation: "spin 1s linear infinite"
        }} />
        <p style={{ color: "#6b7280", fontSize: "14px", fontWeight: "500" }}>Cargando datos...</p>
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div style={{
        padding: "48px",
        textAlign: "center",
        backgroundColor: "#ffffff",
        borderRadius: "16px",
        border: "2px dashed #d1d5db"
      }}>
        <div style={{ fontSize: "48px", marginBottom: "16px" }}>📊</div>
        <p style={{ color: "#6b7280", fontSize: "16px", fontWeight: "500", marginBottom: "8px" }}>No hay datos para mostrar</p>
        <p style={{ color: "#9ca3af", fontSize: "14px" }}>Verifica los filtros seleccionados o intenta con otro rango de fechas</p>
      </div>
    );
  }

  // Toggle expandir/colapsar proveedor
  const toggleProvider = (providerName) => {
    setExpandedProviders((prev) => ({
      ...prev,
      [providerName]: !prev[providerName],
    }));
  };

  // Toggle expandir/colapsar SKU
  const toggleSku = (skuCode) => {
    setExpandedSkus((prev) => ({
      ...prev,
      [skuCode]: !prev[skuCode],
    }));
  };

  // Función para renderizar filas filtradas según estado de expansión
  const renderRows = () => {
    let currentProvider = null;
    let currentSku = null;
    let providerExpanded = true;
    let skuExpanded = true;

    return data.map((row, rowIndex) => {
      if (row.isProviderHeader) {
        currentProvider = row.providerName;
        providerExpanded = expandedProviders[row.providerName] !== false; // Por defecto expandido
        currentSku = null;

        return (
          <tr key={`provider-${rowIndex}`} style={{ backgroundColor: "#f0f9ff", borderBottom: "2px solid #bae6fd" }}>
            <td
              colSpan={headers.length}
              style={{
                padding: "16px 20px",
                fontWeight: "700",
                color: "#0c4a6e",
                fontSize: "15px",
                cursor: "pointer",
                userSelect: "none",
                transition: "background-color 0.2s ease"
              }}
              onClick={() => toggleProvider(row.providerName)}
            >
              <span style={{ marginRight: "8px" }}>
                {providerExpanded ? "▼" : "▶"}
              </span>
              🏢 {row.providerName} <span style={{ fontWeight: "400", color: "#64748b", fontSize: "13px", marginLeft: "8px" }}>(NIT: {row.nit})</span>
            </td>
          </tr>
        );
      }

      // Si el proveedor está colapsado, ocultar sus filas hijas
      if (!providerExpanded) {
        return null;
      }

      if (row.isSkuHeader) {
        currentSku = row.skuCode;
        skuExpanded = expandedSkus[row.skuCode] !== false; // Por defecto expandido

        return (
          <tr key={`sku-${rowIndex}`} style={{ backgroundColor: "#fefce8", borderBottom: "1px solid #fde047" }}>
            <td
              colSpan={headers.length}
              style={{
                padding: "12px 20px",
                fontWeight: "600",
                color: "#854d0e",
                fontSize: "13px",
                paddingLeft: "36px",
                cursor: "pointer",
                userSelect: "none",
                transition: "background-color 0.2s ease"
              }}
              onClick={() => toggleSku(row.skuCode)}
            >
              <span style={{ marginRight: "8px" }}>
                {skuExpanded ? "▼" : "▶"}
              </span>
              📦 {row.skuCode} <span style={{ fontWeight: "400", color: "#78716c", fontSize: "12px", marginLeft: "8px" }}>- {row.skuDescription}</span>
            </td>
          </tr>
        );
      }

      // Si el SKU está colapsado, ocultar sus filas de datos
      if (!skuExpanded) {
        return null;
      }

      // Fila de datos normal
      return (
        <tr
          key={rowIndex}
          style={{
            borderBottom: "1px solid #e5e7eb",
            backgroundColor: rowIndex % 2 === 0 ? "#ffffff" : "#f9fafb",
            transition: "background-color 0.15s ease"
          }}
        >
          <td style={{ padding: "12px 16px", color: "#374151", fontSize: "13px" }}>{row.RecordDate}</td>
          <td style={{ padding: "12px 16px", color: "#374151", fontSize: "13px" }}>{row.Nit}</td>
          <td style={{ padding: "12px 16px", color: "#374151", fontSize: "13px" }}>{row.ProjectName}</td>
          <td style={{ padding: "12px 16px", fontWeight: "600", color: "#111827", fontSize: "13px" }}>{row.CodeSap}</td>
          <td style={{ padding: "12px 16px", color: "#374151", fontSize: "13px" }}>{row.SkuDescription}</td>
          <td style={{ padding: "12px 16px", textAlign: "right", color: "#374151", fontSize: "13px" }}>{row.Inventory}</td>
          <td
            style={{
              padding: "12px 16px",
              textAlign: "right",
              fontWeight: "600",
              color: STATUS_HEX_COLORS[row.ColorLabel] || "#374151",
              fontSize: "13px",
            }}
          >
            {row.PercentInventory}
          </td>
          <td style={{ padding: "12px 16px", textAlign: "right", color: "#374151", fontSize: "13px" }}>{row.Rop}</td>
          <td style={{ padding: "12px 16px", textAlign: "right", color: "#374151", fontSize: "13px" }}>{row.SkuMin}</td>
          <td style={{ padding: "12px 16px", textAlign: "right", color: "#374151", fontSize: "13px" }}>{row.SkuMax}</td>
          <td style={{ padding: "12px 16px", textAlign: "right", color: "#374151", fontSize: "13px" }}>{row.SendQuantity}</td>
          <td style={{ padding: "12px 16px", textAlign: "center" }}>
            <span
              style={{
                display: "inline-block",
                padding: "4px 10px",
                borderRadius: "6px",
                fontSize: "11px",
                fontWeight: "600",
                backgroundColor: STATUS_HEX_COLORS[row.ColorLabel] || "#9ca3af",
                color: "#ffffff",
                textTransform: "uppercase",
                letterSpacing: "0.3px"
              }}
            >
              {row.ColorLabel || "Sin clasificar"}
            </span>
          </td>
          <td style={{ padding: "12px 16px", color: "#6b7280", fontSize: "12px" }}>{row.LastUpdate}</td>
        </tr>
      );
    }).filter(Boolean); // Remover filas null
  };

  return (
    <div style={{ marginBottom: "40px" }}>
      <div style={{ backgroundColor: "#ffffff", borderRadius: "16px", boxShadow: "0 2px 12px rgba(0,0,0,0.06)", border: "1px solid #e5e7eb", overflow: "hidden" }}>
        <div style={{ padding: "20px 24px", borderBottom: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "center", background: "linear-gradient(180deg, #f9fafb 0%, #ffffff 100%)" }}>
          <h4 style={{ margin: 0, color: "#111827", fontSize: "18px", fontWeight: "600" }}>
            📋 Detalle de Inventario por Proveedor
          </h4>
          <button
            onClick={() => {
              // Expandir/colapsar todos
              const allExpanded = Object.values(expandedProviders).every((v) => v !== false);
              const newProviderState = {};
              const newSkuState = {};

              data.forEach((row) => {
                if (row.isProviderHeader) {
                  newProviderState[row.providerName] = !allExpanded;
                }
                if (row.isSkuHeader) {
                  newSkuState[row.skuCode] = !allExpanded;
                }
              });

              setExpandedProviders(newProviderState);
              setExpandedSkus(newSkuState);
            }}
            style={{
              padding: "6px 12px",
              borderRadius: "6px",
              border: "1px solid #d1d5db",
              backgroundColor: "#ffffff",
              color: "#374151",
              fontSize: "12px",
              cursor: "pointer",
              fontWeight: "500",
              transition: "all 0.2s ease",
              boxShadow: "0 1px 2px rgba(0,0,0,0.05)"
            }}
          >
            {Object.values(expandedProviders).every((v) => v !== false) ? "Colapsar Todo" : "Expandir Todo"}
          </button>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
            <thead>
              <tr style={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}>
                {headers.map((header, index) => (
                  <th
                    key={index}
                    style={{
                      padding: "14px 12px",
                      textAlign: "left",
                      color: "#ffffff",
                      fontWeight: "600",
                      fontSize: "12px",
                      textTransform: "uppercase",
                      whiteSpace: "nowrap",
                      borderRight: index < headers.length - 1 ? "1px solid rgba(255,255,255,0.2)" : "none",
                      letterSpacing: "0.5px"
                    }}
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {renderRows()}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
